/* =========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* =========================================================================== */

/*
 * TagArtPanel.jsx — หน้าชุดป้ายโปร (เดิมแท็บ "ภาพบนป้าย" · 29 ต.ค. 2569 · ขั้น 3 แผนป้าย TAG ใหม่)
 *   ภาพ = น้องช้าง + คำโปรที่ AI วาดรวมกัน (v4 · 0041) ผูกกับ สินค้า + คำโปร: อนุมัติแล้วใช้ได้ทุกป้ายทุกสาขาที่ใช้คำเดียวกัน
 *   คำโปรของใบเปลี่ยน (หมดโปร/เลือกสติกเกอร์เอง) = ต้องทำภาพของคำใหม่ · ระหว่างนั้นป้ายวางช้างกับสติกเกอร์แยกกัน ไม่ทับกัน
 *   ดูเป็นป้ายย่อส่วน (รูปสินค้า + น้องช้าง) ทีละใบ → ผ่าน / ไม่ผ่าน + บอกว่าแก้อะไร (ทำใหม่เฉพาะใบนั้น)
 *   OPTIONS /tag_art/list · generate · approve · reject (controllers/tag_art.con.php) · งานทำในคิว worker ทุกนาที
 *   สติกเกอร์คำโปร (ป้ายโปร v3 ขั้น D · 29 ต.ค.): ระบบเลือกตามชนิดราคา · เปลี่ยนเองได้ทีละใบหรือทั้งชุด (OPTIONS /tag_art/sticker)
 *   ป้ายโปร v5 (ประชุมพี่นก 29 ต.ค.): ป้ายขนาดเดียว 7×5 แนวนอน · ป้ายย่อส่วนโชว์ราคาจริง · เลือก 2/3 ราคาต่อชุด (OPTIONS /tag_art/levels) · 31 ต.ค. + ราคาโปรอย่างเดียว
 *   30 ต.ค. (พี่นกขอ): หน่วย Piece / PCs. / Pack. · ให้ AI คิดคำโปร (sticker = ai) · บันทึกคำที่พิมพ์ + ทำภาพในขั้นเดียว
 *            ประวัติภาพต่อสินค้า เลือกภาพรุ่นเก่ากลับมาใช้ได้ไม่เสียเครดิต (OPTIONS /tag_art/history · use)
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import Dialog from '@mui/material/Dialog';
import Drawer from '@mui/material/Drawer';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';

import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import { jobErrorText } from 'layouts/_entrio/jobCreditText';

/* สีป้ายแบบพี่นก V7 (1 พ.ย. 2569) — ตรงกับ tag_promo.print.php */
const RED = '#E22320'; // แถบหัว + กรอบ + ขอบแผง
const PRICE_RED = '#E51C23'; // ตัวเลขราคาเท่านั้น
const NAVY = '#29347A'; // คำโปร จำนวน หน่วย รหัส
const YELLOW = '#FFEA00'; // แผงซื้อหลายชิ้น / ราคาเดียว
/* ป้ายย่อส่วนใช้ฟอนต์เดียวกับไฟล์พิมพ์ (Prompt หนา · ตัวเลข Prompt Black) — index.html ของแอปโหลดแค่ Sarabun
   ไม่โหลด = เบราว์เซอร์ใช้ฟอนต์ระบบ ตัวบางกว่า PDF และชื่อตัดบรรทัดผิดจนซ้อนบรรทัดรายละเอียด (office 1 พ.ย. 2569) */
if (typeof document !== 'undefined' && !document.getElementById('tag-mock-font')) {
  const link = document.createElement('link');
  link.id = 'tag-mock-font';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Prompt:wght@400;700;900&display=swap';
  document.head.appendChild(link);
}
const POLL_MS = 8000; // preventDuplicate ของ backend 5 วิ
const NOTE_MAX = 300;

const STATUS = {
  NONE: { label: 'ยังไม่มีภาพ', color: 'default' },
  ACTIVE: { label: 'กำลังทำ', color: 'info' },
  WAITING: { label: 'รออนุมัติ', color: 'warning' },
  APPROVED: { label: 'อนุมัติแล้ว', color: 'success' },
  FAILED: { label: 'ไม่สำเร็จ', color: 'error' },
};
const FILTERS = ['ALL', 'WAITING', 'NONE', 'ACTIVE', 'APPROVED', 'FAILED'];

/* สิ่งที่สั่งแก้บ่อย — กดแล้วต่อท้ายช่องพิมพ์ */
const QUICK_FIXES = [
  'ของที่ช้างถือไม่ตรงกับสินค้า',
  'หน้าช้างไม่เหมือนต้นแบบ',
  'มีตัวหนังสือในภาพ',
  'ให้ช้างยิ้มร่าเริงกว่านี้',
  'ช้างตัวเล็ก/ใหญ่เกินไป',
];
/* ภาพที่ AI คิดคำเอง — สั่งแก้เรื่องคำได้ */
const AI_FIXES = ['ขอคำโปรคำใหม่', 'คำโปรสะกดผิด', 'คำโปรไม่เข้ากับสินค้า'];

const ERRORS = {
  tag_art_not_ready: 'ระบบภาพบนป้ายยังไม่พร้อม — แจ้งไอที',
  tag_art_mascot_fixed:
    'ชุดนี้ใช้ช้างของแบรนด์ — AI ทำได้เฉพาะรูปสินค้า (ภาพช้าง AI ต้องเปลี่ยนช่อง "ช้างบนป้าย" เป็นช้าง AI)',
  style_not_ready: 'เลือกแบบป้ายยังใช้ไม่ได้ — รอไอทีรัน SQL ชุดใหม่',
  style_unknown: 'ไม่รู้จักแบบป้ายนี้',
  brand_not_ready: 'เลือกช้างทีละใบยังใช้ไม่ได้ — รอไอทีรัน SQL ชุดใหม่',
  tag_art_not_found: 'ไม่พบภาพนี้แล้ว โหลดหน้าใหม่',
  tag_art_not_reviewable: 'สถานะภาพเปลี่ยนแล้ว โหลดหน้าใหม่',
  note_required: 'พิมพ์บอกว่าต้องแก้อะไร',
  note_too_long: `สั่งแก้ได้ไม่เกิน ${NOTE_MAX} ตัวอักษร`,
  busy_try_again: 'มีคนกำลังสั่งทำภาพอยู่ ลองอีกครั้ง',
  nothing_to_generate: 'ทุกสินค้ามีภาพแล้ว หรือกำลังทำอยู่',
  tag_not_found: 'ไม่พบป้ายชุดนี้',
  product_not_found: 'ไม่พบสินค้า',
  mascot_not_found: 'ไม่พบไฟล์ต้นแบบน้องช้าง — แจ้งไอที',
  provider_unavailable: 'ยังไม่ได้ตั้งค่าผู้ให้บริการ AI',
  no_source_image: 'ระบบหาไฟล์รูปสินค้าไม่เจอ · ไม่เสียเครดิต กดลองใหม่ได้',
  mascot_not_ready: 'ระบบแนบรูปช้างยังไม่พร้อม — แจ้งไอที',
  mascot_bad_file: 'เปิดไฟล์รูปไม่ได้ ใช้ PNG หรือ JPG',
  mascot_too_small: 'ตัวช้างในรูปเล็กเกินไป พิมพ์แล้วจะแตก',
  mascot_empty: 'ไม่เจอตัวช้างในรูป (รูปว่างหรือขาวทั้งรูป)',
  image_save_failed: 'บันทึกไฟล์รูปไม่สำเร็จ — แจ้งไอที',
  ai_failed: 'AI ทำภาพไม่สำเร็จ กดลองใหม่ได้',
  ai_sensitive:
    'AI ไม่ยอมทำภาพนี้ (เช่น มีตัวการ์ตูนลิขสิทธิ์บนสินค้า) · ไม่เสียเครดิต — เปลี่ยนเป็นคำสำเร็จรูป/พิมพ์คำเอง แล้วลองใหม่',
  ai_cancelled: 'AI ยกเลิกงาน กดลองใหม่ได้',
  ai_timeout: 'AI ใช้เวลานานเกิน 15 นาที กดลองใหม่ได้',
  worker_stopped: 'ระบบหยุดกลางทาง กดลองใหม่ได้',
  not_login: 'หมดเวลาเข้าสู่ระบบ เข้าสู่ระบบใหม่แล้วลองอีกครั้ง',
  sticker_not_ready: 'ระบบเลือกคำโปรยังไม่พร้อม — แจ้งไอที',
  sticker_unknown: 'ไม่มีคำโปรแบบนี้ โหลดหน้าใหม่',
  product_ids_required: 'ไม่ได้เลือกสินค้า',
  too_many_items: 'เลือกได้ครั้งละไม่เกิน 1,000 รายการ',
  levels_not_ready: 'ระบบเลือกจำนวนราคายังไม่พร้อม — แจ้งไอที',
  levels_unknown: 'จำนวนราคาไม่ถูกต้อง โหลดหน้าใหม่',
  custom_not_ready: 'ระบบพิมพ์คำโปรเองยังไม่พร้อม — แจ้งไอที',
  custom_text_invalid: 'พิมพ์บรรทัดล่าง และไม่เกินบรรทัดละ 20 ตัวอักษร',
  custom_text_english_only: 'คำโปรใช้ภาษาอังกฤษเท่านั้น',
  sticker_text_missing: 'ไม่พบคำโปรที่พิมพ์ไว้ ตั้งคำใหม่แล้วลองอีกครั้ง',
  in_use: 'ป้ายใบนี้ใช้ภาพนี้อยู่แล้ว',
  tag_art_busy: 'กำลังทำภาพใหม่ของคำนี้อยู่ รอให้เสร็จก่อน',
  offer_changed: 'โปรของใบนี้เปลี่ยนแล้ว ภาพคำที่ AI คิดไว้ใช้ไม่ได้',
  file_missing: 'ไฟล์ภาพไม่ครบ ใช้ภาพนี้ไม่ได้',
  not_owner: 'ภาพนี้ยังไม่เคยผ่าน — เลือกได้เฉพาะคนสั่งหรือผู้ดูแล AI',
  rejected_by_reviewer: 'ผู้ตรวจไม่ให้ผ่านภาพนี้ — เลือกได้เฉพาะผู้ดูแล AI',
  newer_waiting: 'มีภาพใหม่ของคนอื่นรออนุมัติอยู่ — ให้คนสั่งหรือผู้ดูแล AI ตรวจก่อน',
  item_edit_not_ready: 'ระบบแก้ชื่อ/ราคาต่อใบยังไม่พร้อม — แจ้งไอทีรัน SQL ชุดใหม่',
  tag_item_not_found: 'ไม่พบสินค้านี้ในชุดแล้ว โหลดหน้าใหม่',
};
export const tagArtErrorText = (code) =>
  ERRORS[code] || jobErrorText(code) || 'ทำรายการไม่สำเร็จ ลองอีกครั้ง';

const statusOf = (it) => {
  const s = it.art ? it.art.status : '';
  if (s === 'QUEUED' || s === 'PROCESSING') return 'ACTIVE';
  if (s === 'WAITING' || s === 'APPROVED' || s === 'FAILED') return s;
  return 'NONE';
};
const num = (v) => Number(v || 0).toLocaleString('th-TH');
/* รูปสินค้า AI (แถวชนิด photo · 29 ต.ค.) — สถานะแบบเดียวกับภาพช้าง */
const photoStatusOf = (it) => {
  const s = it.photo ? it.photo.status : '';
  if (s === 'QUEUED' || s === 'PROCESSING') return 'ACTIVE';
  if (s === 'WAITING' || s === 'APPROVED' || s === 'FAILED') return s;
  return 'NONE';
};
/* AI คิดคำ (ai_ + ชนิดโปร) ยังไม่มีภาพ = สติกเกอร์ตามชนิดโปร แบบไฟล์พิมพ์ (TagArt::fallbackSticker) · ไม่มีโปร = ไม่วาง */
const isAiKind = (kind) => /^ai_/.test(kind || '');
const aiFallback = (kind) => {
  if (!isAiKind(kind)) return kind || ''; // แบบ TagArt::fallbackSticker — ชนิดเดิมค้างระหว่างรอโหลดรายการใหม่
  const offer = String(kind).slice(3);
  return ['buymore', 'mix', 'hotdeal', 'bestprice'].includes(offer) ? offer : '';
};
/* สติกเกอร์ที่ป้ายพิมพ์จริง: เลือกเองก่อน · none = ไม่ใส่ · ai = AI คิดคำ · ว่าง = ระบบเลือกตามชนิดราคา (TagPrice::stickerFor) */
const stickerOf = (it) => {
  if (it.sticker === 'none') return '';
  if (it.sticker === 'ai') return aiFallback(it.art_kind);
  return it.sticker || it.sticker_auto || '';
};
/* คำที่พิมพ์เอง (0043) = รหัส c_ + แฮช · ข้อความ "บรรทัดบน\nบรรทัดล่าง" (บรรทัดบนว่างได้) */
const isCustom = (code) => /^c_[0-9a-f]{10}$/.test(code || '');
const wordLines = (text) => {
  const [a, b] = String(text || '').split('\n');
  return b === undefined ? ['', a] : [a, b];
};
const wordText = (text) => wordLines(text).filter(Boolean).join(' ');
const hasThai = (str) => /[\u0E00-\u0E7F]/.test(str || '');
/* ชื่อสั้นในช่องเลือก (การ์ดแคบ ชื่อเต็มตกขอบ) — รายการเต็มอยู่ในเมนู */
const STICKER_SHORT = {
  buymore: 'BUY MORE',
  mix: 'MIX',
  hotdeal: 'HOT DEAL',
  bestprice: 'BEST PRICE',
  ai: 'AI คิดคำ',
  none: 'ไม่ใส่',
};

/* ป้ายย่อส่วนแบบพี่นก V7 — ผัง 1200×850 หน่วย เท่ากับ promo_tag ใน tag_promo.print.php (ป้าย 70×50 มม. = กว้าง 100cqw)
   แถบหัวแดง 0–201 · โลโก้ในกรอบขาว 30,23 370×160 · ชื่อ 420–1162 · รูปสินค้า 54,238 302×474 · รหัส x205 เส้นฐาน 775
   ช่องคำโปร 425,205 515×157 · ช้าง 953,202 205×205 · แผง 1 ชิ้น 402,370 748×174 · แผงซื้อหลายชิ้น 402,567 748×217 */
const U = (v) => (v / 1200) * 100; // % ของความกว้างป้าย
const V = (v) => (v / 850) * 100; // % ของความสูงป้าย
const cq = (v) => v / 12; // หน่วยผัง → cqw
const BASE = 0.835; // เส้นฐานจากขอบบนกล่อง × ขนาดตัวอักษร (ค่าเดียวกับ PROMO_BASE)
const pct = (b) => ({
  position: 'absolute',
  left: `${b.left}%`,
  top: `${b.top}%`,
  width: `${b.width}%`,
  height: `${b.height}%`,
});
const box = (x, y, w, h) => ({ left: U(x), top: V(y), width: U(w), height: V(h) });
/* ความกว้างข้อความเป็น em แบบเดียวกับ promo_em ในไฟล์พิมพ์ (ตัวเลข 0.62 · ตัวอักษร 0.64 · , . 0.32 · ช่องว่าง 0.28) */
const emOf = (str) =>
  [...String(str)].reduce(
    (w, ch) =>
      w +
      ({ ',': 0.32, '.': 0.32, '!': 0.32, ' ': 0.28, '-': 0.45, '=': 0.45 }[ch] ??
        (/\d/.test(ch) ? 0.62 : 0.64)),
    0
  );
const money = (v) => Number(v || 0).toLocaleString('en-US');
/* ความกว้างชื่อ (em) แบบ promo_em: ไทย 0.6 (sarabun 1.3 เท่า) · สระบน/ล่างไม่กินที่ */
const nameEm = (name) =>
  [...String(name)].reduce((w, ch) => {
    if (/[ัิ-ฺ็-๎]/.test(ch)) return w;
    return w + (/[฀-๿]/.test(ch) ? 0.6 : emOf(ch));
  }, 0);
const hasThaiText = (str) => /[฀-๿]/.test(str || '');
/* ชื่อบนแถบหัวแบบ promo_name_html: ช่วงไทยเป็น Sarabun หนา (Sarabun ของเว็บกว้างเท่า THSarabunNew × 1.3 ในไฟล์พิมพ์ · วัด 1 พ.ย.)
   1–2 คำสั้นท้ายชื่อผูก nbsp ไม่ให้ตกบรรทัดเดียว */
const thaiRuns = (text, thick) => {
  let n = 0;
  return String(text)
    .replace(/ (?=\S{1,3}(?: \S{1,3})?$)/u, ' ')
    .split(/([฀-๿]+(?:\s+[฀-๿]+)*)/u)
    .map((part) => {
      n += 1;
      return hasThaiText(part) ? (
        <span
          key={`t${n}`}
          style={{ fontFamily: 'Sarabun, sans-serif', fontWeight: 700, textShadow: thick }}
        >
          {part}
        </span>
      ) : (
        part
      );
    });
};
const NAME_W = (742 * 70) / 1200; // มม. ช่องชื่อบนแถบหัว
const NAME_H = (170 * 50) / 850;
/** ขนาด (cqw) ของชื่อเด่น/รายละเอียด แบบ promo_name_plan: ชื่อเด่น ≤ 13pt 1–2 บรรทัด · รายละเอียด 1 บรรทัด ≤ 8pt (≥ 4.2) ≤ ชื่อเด่น/1.3 */
const namePlan = (head, detail) => {
  const hem = Math.max(1, nameEm(head));
  const dem = detail ? Math.max(1, nameEm(detail)) : 0;
  const hlh = hasThaiText(head) ? 1.5 : 1.12;
  const dlh = hasThaiText(detail) ? 1.52 : 1.08;
  let best = null;
  (dem ? [1, 2] : [0]).forEach((dl) => {
    const fit = dl ? Math.min(8, (NAME_W * (dl > 1 ? dl * 0.85 : 0.97)) / (dem * 0.3528)) : 0;
    const min = dl === 1 ? 4.2 : 5.5; // #รุ่นย่อยยาว ย่อใน 1 บรรทัด — 2 บรรทัดบีบชื่อเด่นจนจิ๋ว
    if (dl === 1 && fit < min) return;
    [1, 2].forEach((hl) => {
      let dpt = dl ? Math.max(fit, min) : 0;
      let hpt = 0;
      for (let i = 0; i < 3; i += 1) {
        const dh = dl * dpt * 0.3528 * dlh + (dl ? 0.2 : 0);
        hpt = Math.min(
          13,
          (NAME_H - dh) / (hl * 0.3528 * hlh),
          (NAME_W * (hl > 1 ? hl * 0.85 : 0.97)) / (hem * 0.3528)
        );
        dpt = dl ? Math.max(Math.min(min, fit), Math.min(dpt, hpt / 1.3)) : 0;
      }
      if (!best || hpt > best.hpt + 0.05) best = { hpt, hl, dpt, dl };
    });
  });
  const cqw = (pt) => (pt * 0.3528) / 0.7; // 70 มม. = 100cqw
  return { head: cqw(best.hpt), headLines: best.hl, detail: cqw(best.dpt), detailLines: best.dl };
};
/* หน่วยแบบป้ายพี่นก: 1 PC / PCS · 1 PACK / PACKS (TagPrice::unitWord ส่ง Piece/PCs./Pack./Packs. มา) */
const unitOf = (line, qty) =>
  /^pack/i.test(line.unit || '') ? (qty > 1 ? 'PACKS' : 'PACK') : qty > 1 ? 'PCS' : 'PC';
const perUnit = (line) => (/^pack/i.test((line || {}).unit || '') ? 'PER PACK' : 'PER PIECE');
/* ผังราคาเดียว (2 พ.ย. เจ้าของงาน: "แค่ชิ้นเดียวพื้นที่ว่างเยอะไป") — ค่าเดียวกับ PROMO_ONLY_* ในไฟล์พิมพ์ · [x, y, กว้าง, สูง] หน่วยผัง */
const ONLY = {
  img: [40, 222, 370, 500],
  codeX: 225,
  word: [442, 212, 420, 232],
  panel: [440, 455, 712, 330],
  mascot: [872, 198, 280, 257], // เท้าจบที่ขอบบนแผง ไม่ทับตัวหนังสือ
};
const STICKER_LINES = {
  buymore: ['BUY MORE', 'SAVE MORE!'],
  mix: ['ALL YOU CAN', 'MIX!'],
  hotdeal: ['HOT', 'DEAL!'],
  bestprice: ['BEST', 'PRICE!'],
};
/** ผังแผงราคา (กติกาเดียวกับ promo_layout ในไฟล์พิมพ์ · แบบพี่นก 1 พ.ย. 2569)
 *  two = 1 ชิ้น + โปร 1 ขั้น · pack = โปรขั้นเดียวไม่มี 1 ชิ้น · steps = 3 ราคา (หรือโปร 2 ขั้นไม่มี 1 ชิ้น) · only = ราคาเดียว
 *  panels = [ชนิด, y, สูง, จำนวน, ราคา, สีพื้น, ป้ายขั้น] */
const layoutOf = (single, tiers, wordOn) => {
  const t = (i) => [Number(tiers[i].qty), tiers[i].price];
  if (!tiers.length) {
    const g = ONLY.panel; // ผังเดียวทุกกรณี — ช่องคำว่าง = หัว SINGLE PRICE (ไฟล์พิมพ์ $autoOnly)
    return {
      layout: 'only',
      foot: '',
      panels: [['only', g[1], g[3], 1, single ? single.price : 0, '', '']],
    };
  }
  if (single && tiers.length === 1)
    return {
      layout: 'two',
      foot: 'MULTI-BUY PRICE IS THE TOTAL FOR THE QUANTITY SHOWN.',
      panels: [
        ['one', 370, 174, 1, single.price, '', ''],
        ['deal', 567, 217, ...t(0), '', ''],
      ],
    };
  if (!single && tiers.length === 1)
    return {
      layout: 'pack',
      foot: 'PACK PRICE IS THE TOTAL FOR THE QUANTITY SHOWN.',
      panels: [['pack', 370, 414, ...t(0), '', '']],
    };
  const rows = (single ? [{ qty: 1, price: single.price }, ...tiers] : tiers).slice(0, 3);
  const fills = single ? ['white', 'navy', 'yellow'] : ['navy', 'yellow', 'white'];
  const geo =
    rows.length === 3
      ? [
          [370, 133],
          [511, 133],
          [652, 132],
        ]
      : [
          [370, 202],
          [582, 202],
        ];
  return {
    layout: 'steps',
    foot: 'EACH PRICE IS THE TOTAL FOR THE QUANTITY SHOWN.',
    panels: rows.map((r, i) => [
      'step',
      geo[i][0],
      geo[i][1],
      Number(r.qty),
      r.price,
      fills[i],
      `STEP ${i + 1} · ${single && i === 0 ? 'SINGLE' : 'MULTI-BUY'}`,
    ]),
  };
};
/** คำในช่องคำโปรเมื่อตั้ง "อัตโนมัติ" ของผัง pack/steps (คำตามแบบพี่นก · ไฟล์พิมพ์ promo_tag ทำแบบเดียวกัน) · ผังอื่น = '' (ใช้สติกเกอร์ตามชนิดโปร) */
const autoWordOf = (item) => {
  if (item.approved_word_url) return ''; // แบบช้าง AI ที่มีคำในภาพแล้ว = คำในภาพแทนคำ PACK PRICE/3 PRICE STEPS · แยกคำไม่ได้/ภาพไม่มีคำ ไฟล์พิมพ์ยังใช้คำอัตโนมัติ
  const lines = item.price_lines || [];
  const single = item.price_one === 'N' ? null : lines[0];
  const { layout, panels } = layoutOf(single, single ? lines.slice(1) : lines, true);
  if (layout === 'pack') return 'PACK PRICE\nONE TOTAL';
  if (layout === 'steps') return `${panels.length} PRICE STEPS\nCHOOSE YOUR QUANTITY`;
  if (layout === 'only' && lines.length) return `SINGLE PRICE\n${perUnit(lines[0])}`;
  return '';
};

/** ข้อความ 1 บรรทัดวางตามเส้นฐาน (หน่วยผัง) แบบ promo_txt */
function MockText({ x, base, size, align, w, sx, children }) {
  const left = align === 'right' ? x - w : align === 'center' ? x - w / 2 : x;
  return (
    <MDBox
      component='span'
      sx={{
        position: 'absolute',
        left: `${U(left)}%`,
        top: `${V(base - BASE * size)}%`,
        width: `${U(w)}%`,
        fontSize: `${cq(size)}cqw`,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        textAlign: align,
        ...sx,
      }}
    >
      {children}
    </MDBox>
  );
}

MockText.defaultProps = { align: 'left', sx: {} };
MockText.propTypes = {
  x: PropTypes.number.isRequired,
  base: PropTypes.number.isRequired,
  size: PropTypes.number.isRequired,
  align: PropTypes.oneOf(['left', 'right', 'center']),
  w: PropTypes.number.isRequired,
  sx: PropTypes.objectOf(PropTypes.any),
  children: PropTypes.node.isRequired,
};

/** แผงราคาแบบ promo_panel: one / deal (2 ราคา) · pack (ราคาแพ็กอย่างเดียว) · only (ราคาเดียว) · step (แถบขั้นราคา ขาว/น้ำเงิน/เหลือง) · ตัวเลขแดงขอบขาว */
function MockPanel({ kind, py, ph, qty, price, unitLine, fill, stepLabel }) {
  const base = { one: 174, deal: 217, pack: 414, only: 330, step: 133 }[kind];
  const s = Math.min(
    kind === 'one' || kind === 'deal' ? 1.35 : kind === 'step' ? 1.4 : 1.2,
    ph / base
  );
  const bgName = fill || (kind === 'one' ? 'white' : 'yellow');
  const px = kind === 'only' ? ONLY.panel[0] : 402;
  const pw = kind === 'only' ? ONLY.panel[2] : 748;
  const lx = px + 36 * Math.min(1, s);
  const rx = px + pw - 34 * Math.min(1, s);
  const ink = bgName === 'navy' ? '#fff' : NAVY;
  const label = { fontWeight: 700, color: ink };
  const text = `${money(price)}.-`;
  const edge = bgName === 'white' ? 0 : kind === 'step' ? 6 : 7;
  const fit = (w, max, e = edge) => Math.min(max, (w - 2 * e) / (emOf(text) * 1.06));
  const shadow = edge
    ? Array.from({ length: 8 }, (_, i) => {
        const a = (i * Math.PI) / 4;
        return `${cq(edge * Math.cos(a)).toFixed(2)}cqw ${cq(edge * Math.sin(a)).toFixed(
          2
        )}cqw 0 #fff`;
      }).join(', ')
    : 'none';
  const num = { fontWeight: 900, color: PRICE_RED, textShadow: shadow };
  const unit = (q) => unitOf(unitLine, q);
  let inner;
  if (kind === 'step') {
    const qtext = `${qty} ${unit(qty)}`;
    inner = (
      <>
        <MockText x={px + 34} base={py + 30 * s} size={22 * s} w={280} sx={label}>
          {stepLabel}
        </MockText>
        <MockText x={px + 316} base={py + 30 * s} size={22 * s} w={300} sx={label}>
          TOTAL PRICE · THB
        </MockText>
        <MockText
          x={px + 34}
          base={py + 101 * s}
          size={Math.min(61 * s, 300 / Math.max(1, emOf(qtext)))}
          w={320}
          sx={label}
        >
          {qtext}
        </MockText>
        <MockText
          x={px + pw - 27}
          base={py + 116 * s}
          size={fit(pw - 380, 100 * s, 6)}
          align='right'
          w={pw - 340}
          sx={num}
        >
          {text}
        </MockText>
      </>
    );
  } else if (kind === 'only') {
    inner = (
      <>
        <MockText x={lx + 4} base={py + 214 * s} size={118 * s} w={160} sx={label}>
          1
        </MockText>
        <MockText
          x={lx + 6}
          base={py + 282 * s}
          size={Math.min(50 * s, 175 / Math.max(1, emOf(unit(1))))}
          w={180}
          sx={label}
        >
          {unit(1)}
        </MockText>
        <MockText
          x={rx}
          base={py + 236 * s}
          size={fit(pw - 230, 200 * s)}
          align='right'
          w={pw - 200}
          sx={num}
        >
          {text}
        </MockText>
        <MockText x={rx} base={py + 298 * s} size={30 * s} align='right' w={120} sx={label}>
          THB
        </MockText>
      </>
    );
  } else if (kind === 'pack') {
    const pack = true;
    inner = (
      <>
        <MockText x={lx} base={py + 57 * s} size={28 * s} w={320} sx={label}>
          {pack ? 'PACK QUANTITY' : 'SINGLE PRICE'}
        </MockText>
        {pack ? ( // ราคาเดียวไม่มีป้ายขวาบน — เท้าช้างยืนทับตรงนั้น (แบบไฟล์พิมพ์)
          <MockText x={rx} base={py + 57 * s} size={28 * s} align='right' w={320} sx={label}>
            TOTAL PRICE
          </MockText>
        ) : null}
        <MockText
          x={px + 45}
          base={py + 260 * s}
          size={Math.min(160 * s, 200 / Math.max(1, emOf(qty)))}
          w={220}
          sx={label}
        >
          {qty}
        </MockText>
        <MockText
          x={px + 48}
          base={py + 308 * s}
          size={Math.min(42 * s, 215 / Math.max(1, emOf(unit(qty))))}
          w={220}
          sx={label}
        >
          {unit(qty)}
        </MockText>
        <MockText
          x={rx}
          base={py + 282 * s}
          size={fit(pw - 260, 175 * s)}
          align='right'
          w={pw - 230}
          sx={num}
        >
          {text}
        </MockText>
        <MockText x={rx} base={py + ph - 30 * s} size={25 * s} align='right' w={100} sx={label}>
          THB
        </MockText>
      </>
    );
  } else if (kind === 'deal') {
    inner = (
      <>
        <MockText x={lx} base={py + 32 * s} size={25 * s} w={300} sx={label}>
          BUY MORE
        </MockText>
        <MockText x={rx} base={py + 32 * s} size={25 * s} align='right' w={300} sx={label}>
          TOTAL PRICE
        </MockText>
        <MockText
          x={lx + 2}
          base={py + 129 * s}
          size={Math.min(90 * s, 190 / Math.max(1, emOf(qty)))}
          w={200}
          sx={label}
        >
          {qty}
        </MockText>
        <MockText
          x={lx + 5}
          base={py + 180 * s}
          size={Math.min(34 * s, 190 / Math.max(1, emOf(unit(qty))))}
          w={200}
          sx={label}
        >
          {unit(qty)}
        </MockText>
        <MockText
          x={rx}
          base={py + 176 * s}
          size={fit(pw - 230, 174 * s)}
          align='right'
          w={pw - 200}
          sx={num}
        >
          {text}
        </MockText>
        <MockText x={rx} base={py + ph - 15 * s} size={23 * s} align='right' w={100} sx={label}>
          THB
        </MockText>
      </>
    );
  } else {
    inner = (
      <>
        <MockText x={lx} base={py + 42 * s} size={26 * s} w={320} sx={label}>
          SINGLE PRICE
        </MockText>
        <MockText
          x={lx}
          base={py + 126 * s}
          size={Math.min(61 * s, 290 / Math.max(1, emOf(`1 ${unit(1)}`)))}
          w={300}
          sx={label}
        >
          {`1 ${unit(1)}`}
        </MockText>
        <MockText
          x={rx}
          base={py + 141 * s}
          size={fit(pw - 330, 126 * s, 6)}
          align='right'
          w={pw - 300}
          sx={num}
        >
          {text}
        </MockText>
        <MockText x={rx} base={py + ph - 14 * s} size={23 * s} align='right' w={100} sx={label}>
          THB
        </MockText>
      </>
    );
  }
  return (
    <>
      <MDBox
        sx={{
          ...pct(box(px, py, pw, ph)),
          boxSizing: 'border-box',
          bgcolor: { white: '#fff', navy: NAVY, yellow: YELLOW }[bgName],
          border: `${cq(bgName === 'white' ? 5 : 6)}cqw solid ${RED}`,
          borderRadius: `${cq(Math.min(ph / 2, 40))}cqw`,
        }}
      />
      {inner}
    </>
  );
}

MockPanel.defaultProps = { fill: '', stepLabel: '' };
MockPanel.propTypes = {
  kind: PropTypes.oneOf(['one', 'deal', 'pack', 'only', 'step']).isRequired,
  py: PropTypes.number.isRequired,
  ph: PropTypes.number.isRequired,
  qty: PropTypes.number.isRequired,
  price: PropTypes.number.isRequired,
  unitLine: PropTypes.shape({ unit: PropTypes.string }).isRequired,
  fill: PropTypes.string,
  stepLabel: PropTypes.string,
};

/** คำโปรในช่องของมัน (ตัวหนังสือ 2 บรรทัดสีน้ำเงินแบบป้ายพี่นก) · wcq = ความกว้างกล่องเป็น cqw ของกล่องที่ครอบ */
function MockWord({ text, box: b, wcq, small, hk }) {
  const [top, bottom] = wordLines(text);
  const k = hk || wcq / cq(515); // ช่องคำโปรของป้าย = 515 หน่วยผัง · ผังราคาเดียว = สเกลตามความสูงช่อง (แบบไฟล์พิมพ์)
  const size = small
    ? Math.min(cq(54) * k, (wcq * 0.98) / Math.max(1, emOf(top)))
    : Math.min(cq(59) * k, (wcq * 0.98) / Math.max(1, emOf(top), emOf(bottom)));
  const size2 = small ? Math.min(cq(32) * k, (wcq * 0.98) / Math.max(1, emOf(bottom))) : size;
  return (
    <MDBox
      sx={{
        ...pct(b),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        fontWeight: 700,
        color: NAVY,
        fontSize: `${size}cqw`,
        lineHeight: 1.05,
        whiteSpace: 'nowrap',
        textAlign: 'left',
        pl: `${cq(6) * k}cqw`,
      }}
    >
      {top ? <span>{top}</span> : null}
      <MDBox component='span' sx={{ fontSize: `${size2}cqw`, mt: small ? `${cq(14) * k}cqw` : 0 }}>
        {bottom}
      </MDBox>
    </MDBox>
  );
}

MockWord.defaultProps = { small: false, hk: 0 };
MockWord.propTypes = {
  small: PropTypes.bool,
  hk: PropTypes.number,
  text: PropTypes.string.isRequired,
  box: PropTypes.shape({
    left: PropTypes.number,
    top: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  wcq: PropTypes.number.isRequired,
};

/** ป้ายย่อส่วน — วางทุกชิ้นตามผังเดียวกับไฟล์พิมพ์ (promo_tag V7) */
function TagMock({ item, mascotUrl, logoUrl, stickerUrl, word, onClick }) {
  const st = statusOf(item);
  const art = item.art || {};
  const ready = st === 'WAITING' || st === 'APPROVED';
  /* ชื่อเด่น/รายละเอียดคิดที่ server (TagArt::nameParts: ค่าที่ตั้งไว้ก่อน แล้วค่อยกติกา) — จอกับไฟล์พิมพ์ตรงกันเสมอ */
  const head =
    item.name_head ||
    String(item.name || item.sku)
      .replace(/^\(\w+\)\s*/, '')
      .trim();
  const detail = item.name_head ? item.name_detail || '' : '';
  const plan = namePlan(head, detail);
  /* ช่องช้าง = ช้างชี้ของแบรนด์ (แบบพี่นก) · ชุดแบบช้าง AI = ภาพของสินค้าที่แยกเป็นคำโปร + ช้าง (server แยกด้วย artParts ตัวเดียวกับไฟล์พิมพ์)
     โชว์ภาพที่รออนุมัติก่อน (ให้ตรวจว่าลงป้ายแล้วเป็นยังไง) แล้วค่อยภาพที่อนุมัติ — ไฟล์พิมพ์ใช้ที่อนุมัติเท่านั้น */
  const aiParts =
    item.brand_mascot === 'Y' // ใบนี้ตั้งใช้ช้างของแบรนด์ (0050)
      ? null
      : ready && art.ele_url
      ? [art.word_url || '', art.ele_url]
      : item.approved_ele_url
      ? [item.approved_word_url || '', item.approved_ele_url]
      : null;
  const mascot = item.own_mascot_url || (aiParts ? aiParts[1] : mascotUrl); // รูปที่แนบเองมาก่อน (0053)
  const ph = item.photo || {};
  const photo =
    (['WAITING', 'APPROVED'].includes(ph.status) && ph.url) || item.photo_approved_url || '';
  /* ลำดับเดียวกับไฟล์พิมพ์: รูปสินค้า AI → รูปหลักที่ตัดแล้ว → รูปที่เก็บไว้ตอนทำภาพช้าง → รูปหลัก (ผู้ตรวจ 2 พ.ย.) */
  const product = photo || item.crop_url || item.plain_product_url || item.image_url;
  const img = (src, alt, b, pos, outline) =>
    src ? (
      <MDBox
        component='img'
        src={src}
        alt={alt}
        sx={{
          ...pct(b),
          objectFit: 'contain',
          objectPosition: pos || 'center',
          filter: outline
            ? 'drop-shadow(0.35cqw 0 0 #fff) drop-shadow(-0.35cqw 0 0 #fff) drop-shadow(0 0.35cqw 0 #fff) drop-shadow(0 -0.35cqw 0 #fff)'
            : 'none',
        }}
      />
    ) : (
      <MDBox sx={{ ...pct(b), bgcolor: '#f2f2f2', borderRadius: 1 }} />
    );
  const lines = item.price_lines || [];
  const single = item.price_one === 'N' ? null : lines[0]; // ราคาโปรอย่างเดียว = ไม่มีแผง 1 ชิ้น
  const tiers = single ? lines.slice(1) : lines;
  /* ซื้อหลายชิ้นไม่ถูกกว่าซื้อทีละชิ้น = QUANTITY / PRICING แทนคำชวนประหยัด (แบบพี่นก · ไฟล์พิมพ์ทำแบบเดียวกัน) */
  const noSave = item.no_save
    ? item.no_save === 'Y' // server คิดแบบไฟล์พิมพ์ (TagPrice::noSave)
    : Boolean(single) && tiers.some((t) => Number(t.price) >= Number(single.price) * Number(t.qty));
  const kind = stickerOf(item);
  const fallback = aiFallback(kind);
  const custom = isCustom(kind);
  const wordText = custom
    ? word
    : noSave && fallback
    ? 'QUANTITY\nPRICING'
    : fallback && !stickerUrl && STICKER_LINES[fallback]
    ? STICKER_LINES[fallback].join('\n')
    : '';
  /* คำโปรอัตโนมัติ (ไม่ได้ตั้งเอง · "AI คิดคำ" เดิมนับเป็นอัตโนมัติ) + ผัง pack/steps/ราคาเดียว = คำตามแบบพี่นก
     ภาพ AI ที่มีคำ = ใช้คำในภาพ · ภาพช้างอย่างเดียว (ไม่มีคำ) ยังใช้คำอัตโนมัติ (แบบไฟล์พิมพ์) */
  const aiWord = Boolean(aiParts && aiParts[0]);
  const auto = !aiWord && (!item.sticker || item.sticker === 'ai');
  const herWordAll = auto ? autoWordOf(item) : '';
  /* ราคาเดียว: คำอัตโนมัติใช้เมื่อไม่มีสติกเกอร์/คำอื่น (ไฟล์พิมพ์ $autoOnly) */
  const plainWord = aiParts ? '' : (!custom && !noSave && stickerUrl) || wordText;
  /* ราคาเดียว: ช่องคำว่าง (อัตโนมัติ หรือตั้ง "ไม่ใส่") = หัว SINGLE PRICE เสมอ (ไฟล์พิมพ์ $autoOnly) */
  const onlyHead = `SINGLE PRICE\n${perUnit(lines[0])}`;
  const herWord = !tiers.length ? (plainWord || aiWord ? '' : onlyHead) : herWordAll;
  const plan0 = layoutOf(single, tiers, aiWord || Boolean(plainWord) || Boolean(herWord));
  const sticker = aiWord ? aiParts[0] : !herWord && !custom && !noSave ? stickerUrl : '';
  const wordShown = aiWord ? '' : herWord || wordText;
  const { layout, panels, foot } = plan0;
  const printable = item.printable !== 'N';
  const only = layout === 'only';
  const slotGeo = only ? ONLY.word : [425, 205, 515, 157];
  const slot = box(...slotGeo);
  return (
    <MDBox
      component='button'
      type='button'
      onClick={onClick}
      aria-label={`ดูภาพบนป้าย ${item.sku}`}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        display: 'block',
        width: '100%',
        aspectRatio: '7 / 5',
        /* % ของตัวเอง — cqw บนตัว container เทียบกับจอแทนการ์ด (มุมเคยโค้ง 48px บนการ์ดกว้าง 230px) */
        borderRadius: `${U(40)}% / ${V(40)}%`,
        overflow: 'hidden',
        bgcolor: RED, // กรอบแดงผืนเดียวกับแถบหัวแบบไฟล์พิมพ์
        cursor: 'pointer',
        position: 'relative',
        containerType: 'inline-size', // ขนาดตัวหนังสือตามความกว้างการ์ด (cqw)
        fontFamily: 'Prompt, sans-serif',
        '&:focus-visible': { outline: '3px solid #1A73E8', outlineOffset: 2 },
      }}
    >
      <MDBox
        sx={{
          position: 'absolute',
          left: `${U(9)}%`,
          right: `${U(9)}%`,
          top: `${V(201)}%`,
          bottom: `${V(9)}%`,
          bgcolor: '#fff',
          borderRadius: `0 0 ${cq(32)}cqw ${cq(32)}cqw`,
        }}
      />
      {/* โลโก้ในกรอบขาวมุมโค้ง (Save&Smile · Check-in Lowprice/ร้านยา = โลโก้สาขา · TagArt::tagLogo) */}
      <MDBox
        sx={{ ...pct(box(30, 23, 370, 160)), bgcolor: '#fff', borderRadius: `${cq(24)}cqw` }}
      />
      {logoUrl ? img(logoUrl, '', box(40, 31, 350, 144)) : null}
      {/* ชื่อเด่นตัวหนาใหญ่ + รายละเอียด (ขนาด/รุ่นย่อย) สีขาวบนแถบหัว */}
      <MDBox
        title={`${item.sku} ${item.name || ''}`}
        sx={{
          ...pct(box(420, 24, 742, 170)),
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          color: '#fff',
          overflow: 'hidden',
        }}
      >
        {[
          /* ไทยบรรทัดสูงเท่าที่ namePlan เผื่อ — 1.12 ตัดหัววรรณยุกต์ทิ้ง (overflow hidden · "โลชั่น" เหลือ "โลชัน" · เจ้าของงานทัก 30 ต.ค.)
             น้ำหนักตามไฟล์พิมพ์: ชื่อเด่นหนา · รายละเอียดอังกฤษตัวปกติ ไทยหนา (promo_name_html) */
          [head, plan.head, plan.headLines, 700, hasThaiText(head) ? 1.5 : 1.12, '0.04em 0 0 #fff'],
          [detail, plan.detail, plan.detailLines, 400, hasThaiText(detail) ? 1.52 : 1.08, 'none'],
        ]
          .filter(([text, , n]) => text && n)
          .map(([text, size, n, weight, lh, thick]) => (
            <MDBox
              key={weight}
              component='span'
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: n + 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'normal', // ปุ่มรับ white-space: nowrap มาจาก all: unset ของธีม — ไม่ใส่ = ชื่อไม่ขึ้นบรรทัดใหม่ (รีวิว 29 ต.ค.)
                wordBreak: 'break-word',
                fontSize: `${size}cqw`,
                fontWeight: weight,
                lineHeight: lh,
                color: '#fff',
              }}
            >
              {thaiRuns(text, thick)}
            </MDBox>
          ))}
      </MDBox>
      {img(product, item.sku, box(...(only ? ONLY.img : [54, 238, 302, 474])))}
      <MockText
        x={only ? ONLY.codeX : 205}
        base={775}
        size={Math.min(45.5, (only ? 360 : 310) / Math.max(1, emOf(item.code || item.sku) * 1.05))} // ≤ 7.6pt แบบไฟล์พิมพ์ — พนักงานค้นสินค้าจากรหัสนี้
        align='center'
        w={only ? 380 : 330}
        sx={{ fontWeight: 700, color: NAVY }}
      >
        {item.code || item.sku}
      </MockText>
      {panels.map(([k, py, h, qty, price, fill, stepLabel]) => (
        <MockPanel
          key={`${k}${py}`}
          kind={k}
          py={py}
          ph={h}
          qty={qty}
          price={Number(price)}
          unitLine={lines[0] || {}}
          fill={fill}
          stepLabel={stepLabel}
        />
      ))}
      {foot ? (
        <MockText x={1150} base={818} size={20} align='right' w={760} sx={{ color: NAVY }}>
          {foot}
        </MockText>
      ) : null}
      {/* ช่องคำโปร (ล็อกในช่อง) — สติกเกอร์คำน่ารักของเรา · ไม่มีรูป = ตัวหนังสือ */}
      {sticker ? img(sticker, '', slot, 'left center', Boolean(aiParts)) : null}
      {!sticker && wordShown ? (
        <MockWord
          text={wordShown}
          box={slot}
          wcq={cq(slotGeo[2])}
          hk={slotGeo[3] / 157}
          small={Boolean(herWord) && (layout === 'steps' || only)}
        />
      ) : null}
      {/* 2 ราคา: เท้าช้างทับขอบบนแผง 1 ชิ้น · แบบอื่น: ช้างเล็กลงจบเหนือแผง (แบบพี่นก) */}
      {img(
        mascot,
        'น้องช้าง',
        box(...(only ? ONLY.mascot : [953, 200, 205, layout === 'two' ? 205 : 165])),
        'center bottom',
        true
      )}
      {!printable || st === 'ACTIVE' ? (
        <MDBox
          display='flex'
          alignItems='center'
          justifyContent='center'
          sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.75)' }}
        >
          {st === 'ACTIVE' ? (
            <CircularProgress size={28} />
          ) : (
            <MDTypography variant='caption' color='error' fontWeight='medium'>
              ไม่ได้พิมพ์
            </MDTypography>
          )}
        </MDBox>
      ) : null}
    </MDBox>
  );
}

TagMock.propTypes = {
  item: PropTypes.shape({
    sku: PropTypes.string,
    code: PropTypes.string,
    name: PropTypes.string,
    name_head: PropTypes.string,
    name_detail: PropTypes.string,
    image_url: PropTypes.string,
    crop_url: PropTypes.string,
    approved_mascot_url: PropTypes.string,
    no_save: PropTypes.string,
    brand_mascot: PropTypes.string,
    own_mascot_url: PropTypes.string,
    approved_word_url: PropTypes.string,
    approved_ele_url: PropTypes.string,
    printable: PropTypes.string,
    price_one: PropTypes.string,
    price_lines: PropTypes.arrayOf(
      PropTypes.shape({ qty: PropTypes.number, price: PropTypes.number, unit: PropTypes.string })
    ),
    art_kind: PropTypes.string,
    sticker: PropTypes.string,
    plain_product_url: PropTypes.string,
    plain_mascot_url: PropTypes.string,
    photo: PropTypes.shape({ status: PropTypes.string, url: PropTypes.string }),
    photo_approved_url: PropTypes.string,
    art: PropTypes.shape({
      status: PropTypes.string,
      product_url: PropTypes.string,
      mascot_url: PropTypes.string,
      word_url: PropTypes.string,
      ele_url: PropTypes.string,
    }),
  }).isRequired,
  mascotUrl: PropTypes.string.isRequired,
  logoUrl: PropTypes.string,
  stickerUrl: PropTypes.string,
  word: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

TagMock.defaultProps = {
  logoUrl: '',
  stickerUrl: '',
  word: '',
};

const HISTORY_STATE = {
  in_use: { label: 'ใช้อยู่', color: 'success' },
  fallback: { label: 'ใช้อยู่ · คู่สติกเกอร์', color: 'success' }, // ใบนี้ยังไม่มีภาพของคำโปร ป้ายพิมพ์ช้างตัวนี้ + สติกเกอร์
  waiting: { label: 'รออนุมัติ', color: 'warning' },
  approved_before: { label: 'เคยผ่าน', color: 'info' },
  rejected: { label: 'ไม่ผ่าน', color: 'error' },
  unused: { label: 'ไม่ได้ใช้', color: 'default' },
};
const historyLabel = (h) => {
  if (h.kind === 'photo') return 'รูปสินค้า AI';
  if (h.kind === 'none') return 'ช้างอย่างเดียว · ไม่มีคำโปร';
  return h.word || 'คำโปร';
};

/** ประวัติภาพของสินค้า 1 ตัว (พี่นก 30 ต.ค.) — ทุกภาพที่เคยทำ ใหม่ก่อน ดูเป็นป้ายย่อส่วน · เลือกภาพเก่ากลับมาใช้ได้ ไม่เสียเครดิต */
function ArtHistory({
  item,
  tagId,
  token,
  canUse,
  mascotUrl,
  logoUrl,
  stickerUrl,
  word,
  onUsed,
  onNotify,
}) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(0); // id ที่กำลังบันทึก
  const alive = useRef(true);
  const reqSeq = useRef(0); // คำตอบที่มาช้ากว่ารอบใหม่ไม่ทับรายการ (ผู้ตรวจ 30 ต.ค.)
  const fetchRows = () => {
    reqSeq.current += 1;
    const mine = reqSeq.current;
    return FetchData('OPTIONS', '/tag_art/history', token, {
      tag_id: tagId,
      product_id: item.product_id,
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current || mine !== reqSeq.current) return;
        if (r && r.status === 'ok') {
          setRows(r.items || []);
          setErr('');
        } else setErr(tagArtErrorText(r && r.msg));
      })
      .catch(() => {
        if (alive.current && mine === reqSeq.current) setErr('โหลดประวัติภาพไม่สำเร็จ');
      });
  };
  useEffect(() => {
    alive.current = true;
    setRows(null);
    fetchRows();
    return () => {
      alive.current = false;
    };
  }, [item.product_id, tagId]);
  const use = (h) => {
    setSaving(h.id);
    FetchData('OPTIONS', '/tag_art/use', token, {
      tag_id: tagId,
      product_id: item.product_id,
      id: h.id,
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current) return;
        setSaving(0);
        if (!r || r.status !== 'ok') {
          onNotify(tagArtErrorText(r && r.msg), 'error');
          fetchRows();
          onUsed(); // สถานะเปลี่ยนไปแล้ว (มีคนอนุมัติ/ทำใหม่ก่อน) — การ์ดบนจอหลักต้องตามด้วย
          return;
        }
        onNotify(
          r.switched === 'Y'
            ? `ใช้ภาพนี้แล้ว · คำโปรของใบนี้เป็น ${historyLabel(h)}`
            : `ใช้ภาพนี้แล้ว · ${item.sku}`,
          'success'
        );
        fetchRows();
        onUsed();
      })
      .catch(() => {
        if (!alive.current) return;
        setSaving(0);
        onNotify('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง', 'error');
      });
  };
  /* ป้ายย่อส่วนของภาพในประวัติ: ภาพช้าง = แทนภาพของใบ (คำโปรตามภาพ) · รูปสินค้า AI = แทนรูปสินค้า */
  const preview = (h) =>
    h.kind === 'photo'
      ? { ...item, photo: { status: 'APPROVED', url: h.mascot_url }, art: null } // art: null = ภาพช้างที่พิมพ์อยู่ ไม่ใช่วงหมุนของงานที่กำลังทำ
      : {
          ...item,
          art: { status: 'APPROVED', mascot_url: h.mascot_url, product_url: h.product_url },
          art_kind: h.kind,
        };
  if (err && !rows)
    return (
      <MDTypography variant='button' color='error'>
        {err}
      </MDTypography>
    );
  if (!rows)
    return (
      <MDBox p={3} display='flex' justifyContent='center'>
        <CircularProgress size={28} />
      </MDBox>
    );
  if (!rows.length)
    return (
      <MDTypography variant='button' color='text'>
        สินค้านี้ยังไม่มีภาพที่เคยทำ
      </MDTypography>
    );
  return (
    <Grid container spacing={2}>
      {rows.map((h) => {
        const st = HISTORY_STATE[h.state] || HISTORY_STATE.unused;
        return (
          <Grid item key={h.id} xs={12} sm={6} md={4}>
            <TagMock
              item={preview(h)}
              mascotUrl={mascotUrl}
              logoUrl={logoUrl}
              stickerUrl={h.kind === 'photo' ? stickerUrl : ''}
              word={h.kind === 'photo' ? word : ''}
              onClick={() => {}}
            />
            <MDBox mt={0.5} display='flex' alignItems='center' gap={0.5} flexWrap='wrap'>
              <Chip size='small' label={st.label} color={st.color} />
              <MDTypography variant='caption' fontWeight='medium'>
                {historyLabel(h)}
              </MDTypography>
            </MDBox>
            <MDTypography variant='caption' color='text' display='block' lineHeight={1.3}>
              {Func.dateTimeDisplay(h.created_at)}
              {h.created_by_name ? ` · ${h.created_by_name}` : ''}
            </MDTypography>
            {h.note ? (
              <MDTypography variant='caption' color='text' display='block' lineHeight={1.3}>
                สั่งแก้: {h.note}
              </MDTypography>
            ) : null}
            {h.reject_note && h.state === 'rejected' ? (
              <MDTypography variant='caption' color='error' display='block' lineHeight={1.3}>
                ไม่ผ่าน: {h.reject_note}
              </MDTypography>
            ) : null}
            {canUse && h.can_use === 'Y' ? (
              <MDBox mt={0.5}>
                <MDButton
                  size='small'
                  variant='outlined'
                  color='success'
                  disabled={Boolean(saving)}
                  onClick={() => use(h)}
                  aria-label={`ใช้ภาพนี้ ${item.sku} ${historyLabel(h)}`}
                >
                  {saving === h.id ? (
                    <CircularProgress size={14} color='inherit' />
                  ) : (
                    <Icon>check</Icon>
                  )}
                  &nbsp;ใช้ภาพนี้
                </MDButton>
                {h.switch_to ? (
                  <MDTypography variant='caption' color='text' display='block' lineHeight={1.3}>
                    {h.switch_to === 'none'
                      ? 'ใบนี้จะไม่มีคำโปร'
                      : `คำโปรของใบนี้จะเปลี่ยนเป็น ${historyLabel(h)}`}
                  </MDTypography>
                ) : null}
              </MDBox>
            ) : null}
            {canUse && h.can_use !== 'Y' && !['in_use', 'permission_denied'].includes(h.why) ? (
              <MDTypography variant='caption' color='text' display='block' lineHeight={1.3}>
                {tagArtErrorText(h.why)}
              </MDTypography>
            ) : null}
          </Grid>
        );
      })}
    </Grid>
  );
}

ArtHistory.propTypes = {
  item: PropTypes.shape({ product_id: PropTypes.number, sku: PropTypes.string }).isRequired,
  tagId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  token: PropTypes.string.isRequired,
  canUse: PropTypes.bool.isRequired,
  mascotUrl: PropTypes.string.isRequired,
  logoUrl: PropTypes.string.isRequired,
  stickerUrl: PropTypes.string.isRequired,
  word: PropTypes.string.isRequired,
  onUsed: PropTypes.func.isRequired,
  onNotify: PropTypes.func.isRequired,
};

/* ── โหมดตรวจรูป AI เต็มจอ (2 พ.ย. 2569 · อาร์ตบอร์ด 4 ของแบบ Claude Design) ──
   ซ้าย = คิวใบที่รอ (+ ใบที่ตัดสินไปแล้วในรอบนี้) · กลาง = เทียบ "ที่พิมพ์อยู่ตอนนี้" กับ "รูป AI ใหม่" บนป้ายจริง · ขวา = ผ่าน / ไม่ผ่าน (+เหตุผล)
   รูปสินค้า AI ตรวจก่อน แล้วค่อยภาพช้าง+คำ (ชุดแบบช้าง AI) · ตัดสินแล้วข้ามไปใบที่รอถัดไปเอง
   คีย์ลัด Enter ผ่าน · R ไม่ผ่านทำใหม่ · X ไม่ผ่านไม่ทำใหม่ · ← → เลื่อนใบ (ไม่ทำงานตอนพิมพ์ในช่องข้อความ) */
const PHOTO_FIXES = [
  'สีเพี้ยน',
  'ฉลากไม่ตรง',
  'ยังมีป้าย/รหัสแปะอยู่',
  'ขอบสินค้าแหว่ง',
  'เป็นสินค้าคนละตัว',
];
function Kbd({ children }) {
  return (
    <MDBox
      component='kbd'
      sx={{
        display: 'inline-block',
        minWidth: 22,
        px: 0.75,
        lineHeight: '20px',
        border: '1px solid #ced4da',
        borderBottomWidth: 2,
        borderRadius: 1,
        bgcolor: '#fff',
        color: '#495057',
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'inherit',
        textAlign: 'center',
      }}
    >
      {children}
    </MDBox>
  );
}
Kbd.propTypes = { children: PropTypes.node.isRequired };

function TagReview({
  open,
  startId,
  items,
  data,
  fixed,
  canGen,
  busy,
  aiBlocked,
  each,
  eachPhoto,
  stickerUrl,
  wordOf,
  onApprove,
  onReject,
  onApproveAll,
  onEdit,
  onHistory,
  onClose,
  paused,
}) {
  const [cur, setCur] = useState(startId);
  const [note, setNote] = useState('');
  const [decided, setDecided] = useState({}); // {product_id: 'ผ่านแล้ว' | 'ไม่ผ่านแล้ว' | 'ส่งทำใหม่แล้ว'}
  const noteRef = useRef(null);
  useEffect(() => {
    if (open) {
      setCur(startId);
      setNote('');
      setDecided({});
    }
  }, [open, startId]);

  /* เป้าที่ต้องตัดสินของใบนี้: รูปสินค้า AI ก่อน แล้วค่อยภาพช้าง+คำ (เฉพาะชุดแบบช้าง AI) */
  const targetOf = (it) => {
    if (!it || it.printable === 'N' || !canGen) return null;
    const ph = it.photo || {};
    const art = it.art || {};
    if (photoStatusOf(it) === 'WAITING' && ph.can_review === 'Y')
      return { kind: 'photo', id: ph.id, url: ph.url, note: ph.note };
    if (!fixed && statusOf(it) === 'WAITING' && art.can_review === 'Y')
      return { kind: 'art', id: art.id, url: art.mascot_url, note: art.note, art };
    return null;
  };
  const waiting = items.filter((it) => targetOf(it));
  const queue = items.filter((it) => targetOf(it) || decided[it.product_id]);
  const item = items.find((it) => it.product_id === cur) || null;
  const target = targetOf(item);
  const pos = waiting.findIndex((it) => it.product_id === cur);
  const nextWaiting = (from) => {
    const idx = items.findIndex((it) => it.product_id === from);
    return (
      items.slice(idx + 1).find((it) => targetOf(it)) ||
      items.slice(0, Math.max(0, idx)).find((it) => targetOf(it)) ||
      null
    );
  };
  const blurClicked = () => {
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
  };
  const go = (pid) => {
    setCur(pid);
    setNote('');
    blurClicked(); // โฟกัสไม่ค้างที่ปุ่มที่เพิ่งคลิก — Enter ใบถัดไปยังเป็น "ผ่าน" ตามที่จอบอก
  };
  const stepQueue = (d) => {
    const list = queue.length ? queue : items;
    const i = list.findIndex((it) => it.product_id === cur);
    const n = list[i + d];
    if (n) go(n.product_id);
  };
  const settle = (label) => {
    const from = cur;
    setDecided((m) => ({ ...m, [from]: label }));
    const n = nextWaiting(from);
    if (n && n.product_id !== from) go(n.product_id);
    else setNote('');
  };
  const doApprove = () => {
    if (!target || busy) return;
    onApprove([target.id]).then((r) => {
      if (r) settle('ผ่านแล้ว');
    });
  };
  const doReject = (redo) => {
    if (!target || busy) return;
    const text = note.trim();
    if (redo && !text) {
      if (noteRef.current) noteRef.current.focus();
      return;
    }
    if (redo && aiBlocked) return;
    onReject(item, target.kind === 'photo' ? target.id : 0, text, redo).then((r) => {
      if (r) settle(redo ? 'ส่งทำใหม่แล้ว' : 'ไม่ผ่านแล้ว');
    });
  };
  const onKey = (e) => {
    const tag = (e.target && e.target.tagName ? e.target.tagName : '').toLowerCase();
    if (paused || tag === 'textarea' || tag === 'input' || tag === 'select') return;
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.repeat && e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return; // กดค้างไม่ตัดสินหลายใบรวดเดียว
    /* Enter บนปุ่มที่โฟกัสอยู่ = กดปุ่มนั้น ไม่ใช่ "ผ่าน" (กันอนุมัติผิดใบตอนกด ถัดไป/ประวัติ) */
    if (e.key === 'Enter' && e.target.closest && e.target.closest('button,a,[role=button]')) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      doApprove();
    } else if (e.key === 'r' || e.key === 'R') {
      e.preventDefault();
      doReject(true);
    } else if (e.key === 'x' || e.key === 'X') {
      e.preventDefault();
      doReject(false);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      stepQueue(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      stepQueue(-1);
    }
  };

  /* คีย์ลัดฟังที่ window (MUI Dialog ไม่ส่ง keydown ให้ Paper เสมอ) · ผูกใหม่ทุก render ให้เห็น state ล่าสุด */
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const mock = (it, sx) => (
    <MDBox sx={{ maxWidth: 520, mx: 'auto', ...sx }}>
      <TagMock
        item={it}
        mascotUrl={data.tag_mascot_url || data.mascot_url}
        logoUrl={data.logo_url || ''}
        stickerUrl={stickerUrl(it)}
        word={wordOf(it)}
        onClick={() => {}}
      />
    </MDBox>
  );
  /* "ที่พิมพ์อยู่ตอนนี้" = ป้ายใบเดียวกันโดยไม่นับภาพที่รอ (TagMock ถอยไปรูปที่อนุมัติเดิม → รูปในระบบ) */
  const printedNow = item
    ? target && target.kind === 'photo'
      ? { ...item, photo: null }
      : target
      ? { ...item, art: null }
      : item
    : null;
  const fixes =
    target && target.kind === 'photo'
      ? PHOTO_FIXES
      : [...QUICK_FIXES, ...(item && isAiKind(item.art_kind) ? AI_FIXES : [])];
  const checklist =
    target && target.kind === 'photo'
      ? [
          'ฉลาก ตัวหนังสือ สี ตรงสินค้าจริง',
          'ไม่มีป้ายราคา/รหัสที่ร้านแปะติดมา',
          'ขอบสินค้าไม่แหว่ง พื้นขาวสะอาด',
        ]
      : ['ของที่ช้างถือตรงกับสินค้า', 'คำโปรสะกดถูก ไม่มีตัวหนังสืออื่น', 'หน้าช้างเหมือนต้นแบบ'];
  const meta = target && target.kind === 'art' && target.art ? target.art : null;
  const stateText = (it) => {
    const t = targetOf(it);
    if (!t)
      return [decided[it.product_id] || 'ไม่มีอะไรรอ', decided[it.product_id] ? 'success' : 'text'];
    return [
      it.product_id === cur
        ? 'กำลังตรวจ'
        : t.kind === 'photo'
        ? 'รูปสินค้า AI รอ'
        : 'ภาพช้าง + คำ รอ',
      'warning',
    ];
  };
  const canApproveAll = waiting.length > 1;

  return (
    <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ sx: { bgcolor: '#f0f2f5' } }}>
      {item ? (
        <MDBox display='flex' flexDirection='column' sx={{ minHeight: '100%' }}>
          {/* แถบบน */}
          <MDBox
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            flexWrap='wrap'
            gap={2}
            px={3}
            sx={{ bgcolor: '#344767', color: '#fff', minHeight: 56 }}
          >
            <MDBox display='flex' alignItems='center' gap={2} flexWrap='wrap'>
              <MDButton variant='text' color='white' size='small' onClick={onClose}>
                <Icon>arrow_back</Icon>&nbsp;กลับหน้าชุดป้าย
              </MDButton>
              <MDTypography variant='h6' color='white'>
                ตรวจรูป AI ก่อนพิมพ์
              </MDTypography>
              <MDTypography variant='button' color='white' sx={{ opacity: 0.85 }}>
                {pos >= 0
                  ? `ใบที่ ${num(pos + 1)} จาก ${num(waiting.length)} ที่รอ`
                  : waiting.length
                  ? `เหลือ ${num(waiting.length)} ใบที่รอ`
                  : 'ไม่มีใบที่รอแล้ว'}
              </MDTypography>
            </MDBox>
            <MDBox
              display='flex'
              alignItems='center'
              gap={1}
              flexWrap='wrap'
              sx={{ fontSize: 13, opacity: 0.9, display: { xs: 'none', md: 'flex' } }}
            >
              <span>คีย์ลัด</span>
              <Kbd>Enter</Kbd>
              <span>ผ่าน</span>
              <Kbd>R</Kbd>
              <span>ไม่ผ่าน ทำใหม่</span>
              <Kbd>X</Kbd>
              <span>ไม่ผ่าน</span>
              <Kbd>→</Kbd>
              <span>ใบถัดไป</span>
            </MDBox>
          </MDBox>

          <MDBox display='flex' flexWrap='wrap' sx={{ flex: 1, minHeight: 0 }}>
            {/* คิว */}
            <MDBox
              component='nav'
              aria-label='รายการที่รอตรวจ'
              sx={{
                /* จอแคบ: รูปกับปุ่มตัดสินขึ้นก่อน คิวอยู่ท้ายสุด */
                order: { xs: 3, md: 0 },
                width: { xs: '100%', md: 280 },
                flex: 'none',
                bgcolor: '#fff',
                borderRight: { xs: 0, md: '1px solid #dee2e6' },
                borderTop: { xs: '1px solid #dee2e6', md: 0 },
                maxHeight: { xs: 320, md: 'calc(100vh - 56px)' },
                overflowY: 'auto',
              }}
            >
              <MDBox
                px={2}
                py={1.5}
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                gap={1}
                sx={{ borderBottom: '1px solid #f0f2f5' }}
              >
                <MDTypography variant='button' fontWeight='bold'>
                  รอตรวจ {num(waiting.length)} · เสร็จ {num(Object.keys(decided).length)}
                </MDTypography>
                {canApproveAll ? (
                  <MDButton
                    size='small'
                    variant='outlined'
                    color='success'
                    disabled={busy}
                    onClick={() => onApproveAll(waiting.map((it) => targetOf(it).id))}
                  >
                    ผ่านทั้งหมด
                  </MDButton>
                ) : null}
              </MDBox>
              {queue.map((it) => {
                const [text, color] = stateText(it);
                const active = it.product_id === cur;
                return (
                  <MDBox
                    key={it.product_id}
                    component='button'
                    type='button'
                    onClick={() => go(it.product_id)}
                    display='flex'
                    alignItems='center'
                    gap={1.25}
                    px={2}
                    py={1.25}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      border: 0,
                      borderBottom: '1px solid #f0f2f5',
                      borderLeft: `4px solid ${active ? '#fb8c00' : 'transparent'}`,
                      bgcolor: active ? '#fff4e5' : '#fff',
                      cursor: 'pointer',
                      font: 'inherit',
                    }}
                    aria-current={active ? 'true' : undefined}
                  >
                    <MDBox
                      component='img'
                      src={it.image_url || ''}
                      alt=''
                      sx={{
                        width: 52,
                        height: 40,
                        objectFit: 'contain',
                        borderRadius: 1,
                        bgcolor: '#eef0f3',
                        flex: 'none',
                      }}
                    />
                    <MDBox sx={{ minWidth: 0, flex: 1 }}>
                      <MDTypography variant='button' fontWeight='bold' noWrap display='block'>
                        {it.code || it.sku}
                      </MDTypography>
                      <MDTypography variant='caption' color='text' noWrap display='block'>
                        {it.name_head || it.name}
                      </MDTypography>
                      <MDTypography variant='caption' color={color} display='block'>
                        {text}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              })}
              <MDTypography variant='caption' color='text' display='block' px={2} py={1.5}>
                ใบที่ยังไม่ผ่าน ป้ายจะพิมพ์ด้วยรูปที่อนุมัติเดิมหรือรูปในระบบไปก่อน ไม่ถูกกันพิมพ์
              </MDTypography>
            </MDBox>

            {/* เทียบรูป */}
            <MDBox
              component='main'
              sx={{ flex: '1 1 480px', minWidth: 0 }}
              px={3}
              py={2.5}
              display='flex'
              flexDirection='column'
              gap={2}
            >
              <MDBox
                display='flex'
                alignItems='baseline'
                justifyContent='space-between'
                flexWrap='wrap'
                gap={1}
              >
                <MDBox>
                  <MDTypography variant='h5'>
                    {item.code || item.sku} · {item.name_head || item.name}
                  </MDTypography>
                  <MDTypography variant='caption' color='text'>
                    {[item.name_detail, item.name !== item.name_head ? item.name : '']
                      .filter(Boolean)
                      .join(' · ')}
                  </MDTypography>
                </MDBox>
                {meta ? (
                  <MDTypography variant='caption' color='text'>
                    {[
                      meta.created_by_name ? `สั่งโดย ${meta.created_by_name}` : '',
                      meta.created_at ? Func.dateTimeDisplay(meta.created_at) : '',
                      meta.credits_used ? `ใช้ไป ${num(meta.credits_used)} เครดิต` : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </MDTypography>
                ) : null}
              </MDBox>
              {target ? (
                <Grid container spacing={2.5} alignItems='flex-start'>
                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant='button'
                      color='text'
                      fontWeight='bold'
                      display='block'
                      mb={0.5}
                    >
                      ที่พิมพ์อยู่ตอนนี้
                      {target.kind === 'photo'
                        ? item.photo_approved_url
                          ? ' · รูป AI ที่ผ่านก่อนหน้า'
                          : ' · รูปในระบบ'
                        : item.approved_ele_url
                        ? ' · ภาพที่ผ่านก่อนหน้า'
                        : ' · ช้างของแบรนด์'}
                    </MDTypography>
                    <MDBox sx={{ opacity: 0.8 }}>{mock(printedNow)}</MDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant='button'
                      fontWeight='bold'
                      display='block'
                      mb={0.5}
                      sx={{ color: '#b35c00' }}
                    >
                      {target.kind === 'photo' ? 'รูปสินค้า AI ใหม่' : 'ภาพช้าง + คำโปรใหม่'} ·
                      รออนุมัติ
                    </MDTypography>
                    {mock(item, { boxShadow: '0 0 0 4px #ffd9a8', borderRadius: 2 })}
                  </Grid>
                  {target.url ? (
                    <Grid item xs={12}>
                      <MDBox display='flex' alignItems='center' gap={1.5} flexWrap='wrap'>
                        <MDBox
                          component='img'
                          src={target.url}
                          alt='ภาพที่ AI ทำ'
                          sx={{
                            maxHeight: 150,
                            maxWidth: 300,
                            objectFit: 'contain',
                            borderRadius: 1,
                            bgcolor: '#fff',
                            border: '1px solid #dee2e6',
                          }}
                        />
                        <MDTypography variant='caption' color='text'>
                          ภาพที่ AI ทำ (ต้นฉบับ) · ที่ลงป้ายคือภาพนี้หลังตัดพื้น/แยกคำกับช้าง
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  ) : null}
                </Grid>
              ) : (
                <MDBox>
                  <MDTypography variant='button' color='text' display='block' mb={1}>
                    ใบนี้ไม่มีรูปที่รออนุมัติ
                    {item.printable === 'N' ? ' · ไม่ได้พิมพ์ (ไม่มีราคาที่สาขานี้)' : ''}
                  </MDTypography>
                  {mock(item)}
                </MDBox>
              )}
              <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                {target && target.url ? (
                  <MDButton
                    size='small'
                    variant='outlined'
                    color='dark'
                    onClick={() => window.open(target.url, '_blank', 'noopener')}
                  >
                    <Icon>open_in_new</Icon>&nbsp;ดูรูปเต็ม
                  </MDButton>
                ) : null}
                {Number(item.history_count) > 0 ? (
                  <MDButton
                    size='small'
                    variant='outlined'
                    color='dark'
                    onClick={() => onHistory(item.product_id)}
                  >
                    <Icon>history</Icon>&nbsp;ประวัติรูปของสินค้านี้ · {num(item.history_count)}
                  </MDButton>
                ) : null}
                <MDButton
                  size='small'
                  variant='outlined'
                  color='dark'
                  onClick={() => onEdit(item.product_id)}
                >
                  <Icon>edit</Icon>&nbsp;แก้ใบนี้
                </MDButton>
                {target && target.note ? (
                  <MDTypography variant='caption' color='text'>
                    ทำตามคำสั่งแก้: {target.note}
                  </MDTypography>
                ) : null}
                <MDTypography variant='caption' color='text'>
                  อนุมัติครั้งเดียว ใช้กับทุกชุดทุกสาขาที่มีสินค้านี้
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* ตัดสิน */}
            <MDBox
              component='aside'
              aria-label='ตัดสิน'
              sx={{
                width: { xs: '100%', md: 340 },
                flex: { xs: 'none', md: '1 0 300px' },
                maxWidth: { xs: 'none', md: 380 },
                bgcolor: '#fff',
                borderLeft: { xs: 0, md: '1px solid #dee2e6' },
              }}
              px={3}
              py={2.5}
              display='flex'
              flexDirection='column'
              gap={1.5}
            >
              {target ? (
                <>
                  <MDTypography variant='h6'>ตรวจอะไรบ้าง</MDTypography>
                  <MDBox
                    component='ul'
                    sx={{ m: 0, pl: 2.25, color: '#495057', fontSize: 14, lineHeight: 1.7 }}
                  >
                    {checklist.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </MDBox>
                  <MDButton
                    color='success'
                    size='large'
                    disabled={busy}
                    onClick={doApprove}
                    sx={{ justifyContent: 'center', gap: 1 }}
                  >
                    <Icon>check</Icon>&nbsp;ผ่าน · ใช้รูปนี้&nbsp;<Kbd>Enter</Kbd>
                  </MDButton>
                  <MDBox
                    display='flex'
                    flexDirection='column'
                    gap={1}
                    pt={1}
                    sx={{ borderTop: '1px solid #f0f2f5' }}
                  >
                    <MDTypography variant='button' fontWeight='bold'>
                      ไม่ผ่าน · ต้องแก้อะไร
                    </MDTypography>
                    <MDBox display='flex' flexWrap='wrap' gap={0.5}>
                      {fixes.map((q) => (
                        <Chip
                          key={q}
                          size='small'
                          variant='outlined'
                          label={q}
                          onClick={() => {
                            blurClicked();
                            setNote((n) =>
                              (n.trim() ? `${n.trim()} · ${q}` : q).slice(0, NOTE_MAX)
                            );
                          }}
                        />
                      ))}
                    </MDBox>
                    <TextField
                      inputRef={noteRef}
                      fullWidth
                      multiline
                      minRows={2}
                      size='small'
                      label='บอก AI เพิ่ม (ถ้าจะทำใหม่)'
                      value={note}
                      onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                    />
                    <MDButton
                      variant='outlined'
                      color='error'
                      disabled={busy || !note.trim() || Boolean(aiBlocked)}
                      title={note.trim() ? '' : 'เลือกหรือพิมพ์สิ่งที่ต้องแก้ก่อน'}
                      onClick={() => doReject(true)}
                      sx={{ justifyContent: 'center', gap: 1 }}
                    >
                      <Icon>close</Icon>&nbsp;ไม่ผ่าน · ทำใหม่ ≈{' '}
                      {target && target.kind === 'photo' ? eachPhoto : each} เครดิต&nbsp;
                      <Kbd>R</Kbd>
                    </MDButton>
                    <MDButton
                      variant='text'
                      color='dark'
                      disabled={busy}
                      onClick={() => doReject(false)}
                      sx={{ justifyContent: 'center', gap: 1, bgcolor: '#f0f2f5' }}
                    >
                      ไม่ผ่าน · ใช้รูปเดิมต่อไป (ไม่เสียเครดิต)&nbsp;<Kbd>X</Kbd>
                    </MDButton>
                  </MDBox>
                </>
              ) : (
                <MDTypography variant='button' color='text'>
                  {decided[item.product_id]
                    ? `ใบนี้${decided[item.product_id]}`
                    : 'ใบนี้ไม่มีรูปที่ต้องตัดสิน'}
                  {waiting.length ? ' · กด → ไปใบที่รอ' : ''}
                </MDTypography>
              )}
              <MDBox
                mt='auto'
                pt={1.5}
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                gap={1}
              >
                <MDButton
                  size='small'
                  variant='outlined'
                  color='dark'
                  onClick={() => stepQueue(-1)}
                >
                  <Icon>chevron_left</Icon>&nbsp;ก่อนหน้า
                </MDButton>
                <MDTypography variant='caption' color='text'>
                  ข้ามใบนี้ไปก่อนได้
                </MDTypography>
                <MDButton size='small' variant='outlined' color='dark' onClick={() => stepQueue(1)}>
                  ถัดไป&nbsp;<Icon>chevron_right</Icon>
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
    </Dialog>
  );
}
TagReview.defaultProps = { startId: null, aiBlocked: '', paused: false };
TagReview.propTypes = {
  open: PropTypes.bool.isRequired,
  startId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.shape({
    logo_url: PropTypes.string,
    tag_mascot_url: PropTypes.string,
    mascot_url: PropTypes.string,
  }).isRequired,
  fixed: PropTypes.bool.isRequired,
  canGen: PropTypes.bool.isRequired,
  busy: PropTypes.bool.isRequired,
  aiBlocked: PropTypes.string,
  each: PropTypes.number.isRequired,
  eachPhoto: PropTypes.number.isRequired,
  stickerUrl: PropTypes.func.isRequired,
  wordOf: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onApproveAll: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onHistory: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  paused: PropTypes.bool,
};

/* หน้าชุดป้ายโปรทั้งหน้า (2 พ.ย. 2569 · รวมแท็บ "ข้อมูล" + "ภาพบนป้าย" ตามแบบ Claude Design):
   ตั้งค่าทั้งชุด (ช่องจาก Tag.jsx ผ่าน `settings` + แบบป้าย/ราคาบนป้าย/คำโปรทุกใบ) · ค้นหา + กรองสถานะ · "ทำให้ทุกใบ" · ปุ่มเพิ่มสินค้า (`addButtons`)
   การ์ดเรียงตามแผ่น/ช่องที่พิมพ์จริง (1 การ์ด = 1 แถว tags_list จาก `rows`) มีป้ายสถานะ + ผ่าน/ไม่ผ่านบนการ์ดที่รอ · กดการ์ด = หน้าต่างแก้ป้ายใบนั้น
   ติ๊กหลายใบ = แถบทำทีเดียว · `jump` จากแถบ "ก่อนพิมพ์" ของ Tag.jsx (ตรวจรูปที่รอ / ดูใบที่ไม่ได้พิมพ์) */
const SHEETS_PER_PAGE = 2;
const CARD_FILTERS = {
  ALL: 'ทั้งหมด',
  WAITING: 'รออนุมัติ',
  ACTIVE: 'กำลังทำรูป',
  FAILED: 'ไม่สำเร็จ',
  CUSTOM: 'ตั้งเองต่อใบ',
  UNPRINTED: 'ไม่ได้พิมพ์',
};
const BADGE = {
  READY: { label: 'พร้อมพิมพ์', color: 'success' },
  CUSTOM: { label: 'พร้อมพิมพ์ · ตั้งเอง', color: 'info' },
  WAITING: { label: 'รูป AI รออนุมัติ', color: 'warning' },
  ACTIVE: { label: 'กำลังทำรูป', color: 'info' },
  FAILED: { label: 'ทำรูปไม่สำเร็จ', color: 'error' },
  UNPRINTED: { label: 'ไม่ได้พิมพ์', color: 'error' },
};
/* แนบรูปช้างเอง (0053): บอกขนาดก่อนเลือกไฟล์ · ตัวอย่างบนป้ายจริงก่อนบันทึก · ขนาดพิมพ์คิดจากช่องช้างในไฟล์พิมพ์ (ผัง 1200×850 ย่อเป็น 70×50 มม.) */
const MASCOT_WORK_PX = 3000; // ขนาดที่ใช้หาขอบในเบราว์เซอร์
const MASCOT_SEND_PX = 1200; // ย่อตัวช้างก่อนส่ง ไม่ชน post_max_size 8M
const MASCOT_MIN_PX = 200; // เท่า TagArt::OWN_MASCOT_MIN
const MASCOT_GOOD_PX = 320; // เท่าความละเอียดที่ไฟล์พิมพ์ใช้ (PROMO_MASCOT_PX)

function MascotDialog({
  ask,
  token,
  tagId,
  mascotUrl,
  logoUrl,
  stickerUrl,
  word,
  onClose,
  onDone,
}) {
  const [file, setFile] = useState(null); // {url, w, h}
  const [problem, setProblem] = useState('');
  const [sending, setSending] = useState(false);
  useEffect(() => {
    setFile(null);
    setProblem('');
    setSending(false);
  }, [ask]);
  if (!ask) return null;

  /* ตัดขอบว่าง/ขาวรอบตัวแบบเดียวกับ server ตัวอย่างจึงตรงกับที่พิมพ์ · รูปปกติไม่กี่สิบมิลลิวินาที */
  const pick = (f) => {
    setProblem('');
    const src = URL.createObjectURL(f);
    const im = new Image();
    im.onerror = () => {
      URL.revokeObjectURL(src);
      setFile(null);
      setProblem(ERRORS.mascot_bad_file);
    };
    im.onload = () => {
      /* ตัดขอบก่อนย่อ — ขนาดที่เช็คคือตัวช้างจริงที่ส่ง ไม่ใช่ทั้งภาพ (รูปถ่ายใบใหญ่ที่ช้างเล็กจะได้เตือนตั้งแต่ตรงนี้) */
      const k = Math.min(1, MASCOT_WORK_PX / Math.max(im.naturalWidth, im.naturalHeight));
      const w = Math.max(1, Math.round(im.naturalWidth * k));
      const h = Math.max(1, Math.round(im.naturalHeight * k));
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d');
      ctx.drawImage(im, 0, 0, w, h);
      URL.revokeObjectURL(src);
      const d = ctx.getImageData(0, 0, w, h).data;
      let [x0, y0, x1, y1] = [w, h, -1, -1];
      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          const i = (y * w + x) * 4;
          if (d[i + 3] > 27 && !(d[i] >= 240 && d[i + 1] >= 240 && d[i + 2] >= 240)) {
            x0 = Math.min(x0, x);
            x1 = Math.max(x1, x);
            y0 = Math.min(y0, y);
            y1 = Math.max(y1, y);
          }
        }
      }
      if (x1 < 0) {
        setFile(null);
        setProblem(ERRORS.mascot_empty);
        return;
      }
      const cw = x1 - x0 + 1;
      const ch = y1 - y0 + 1;
      const ks = Math.min(1, MASCOT_SEND_PX / Math.max(cw, ch));
      const out = document.createElement('canvas');
      out.width = Math.max(1, Math.round(cw * ks));
      out.height = Math.max(1, Math.round(ch * ks));
      out.getContext('2d').drawImage(c, x0, y0, cw, ch, 0, 0, out.width, out.height);
      const url = out.toDataURL('image/png');
      if (url.length > 7500000) {
        setFile(null);
        setProblem('ไฟล์ใหญ่เกินไป ลองลบรายละเอียดพื้นหลังออก หรือย่อรูปก่อน');
        return;
      }
      setFile({ url, w: out.width, h: out.height });
    };
    im.src = src;
  };

  const send = (body) => {
    setSending(true);
    setProblem('');
    return FetchData('OPTIONS', '/tag_art/mascot_file', token, {
      tag_id: tagId,
      product_ids: ask.ids,
      ...body,
      _t: Date.now(),
    })
      .then((r) => {
        setSending(false);
        if (r && r.status === 'ok') onDone(body.remove === 'Y', Number(r.count) || ask.ids.length);
        else setProblem(tagArtErrorText(r && r.msg));
      })
      .catch(() => {
        setSending(false);
        setProblem('บันทึกไม่สำเร็จ ลองอีกครั้ง');
      });
  };

  const side = file ? Math.max(file.w, file.h) : 0;
  const tooSmall = file && side < MASCOT_MIN_PX;
  const small = file && !tooSmall && side < MASCOT_GOOD_PX;
  const notSquare = file && Math.max(file.w, file.h) / Math.max(1, Math.min(file.w, file.h)) > 1.3;
  const spec = [
    ['ขนาดรูป', 'แนะนำ 1000 × 1000 px (จัตุรัส 1:1) · ต่ำสุด 200 px'],
    ['ไฟล์', 'PNG พื้นโปร่งใส · JPG/WebP ได้ พื้นขาวรอบตัวระบบลบให้'],
    ['ขนาดบนป้าย', '≈ 12 × 12 มม. · ป้ายราคาเดียว ≈ 16 × 15 มม. · แพ็ก/3 ราคา ≈ 12 × 10 มม.'],
    ['การวาง', 'เท้าอยู่ขอบล่างของรูป · ขอบว่างรอบตัวระบบตัดให้ · ขอบขาวรอบตัวระบบใส่ให้'],
  ];
  return (
    <Dialog open onClose={sending ? undefined : onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        แนบรูปช้างใหม่ ·{' '}
        {ask.bulk ? `ทุกใบในชุด (${num(ask.ids.length)} สินค้า)` : `${num(ask.ids.length)} สินค้า`}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <MDBox component='table' sx={{ borderCollapse: 'collapse', width: '100%', mb: 1.5 }}>
              <tbody>
                {spec.map(([k, v]) => (
                  <tr key={k}>
                    <MDBox
                      component='td'
                      sx={{ py: 0.5, pr: 1, verticalAlign: 'top', whiteSpace: 'nowrap' }}
                    >
                      <MDTypography variant='caption' fontWeight='bold'>
                        {k}
                      </MDTypography>
                    </MDBox>
                    <MDBox component='td' sx={{ py: 0.5 }}>
                      <MDTypography variant='caption' color='text'>
                        {v}
                      </MDTypography>
                    </MDBox>
                  </tr>
                ))}
              </tbody>
            </MDBox>
            <MDButton
              component='label'
              variant='outlined'
              color='info'
              size='small'
              disabled={sending}
            >
              <Icon>upload_file</Icon>&nbsp;{file ? 'เปลี่ยนไฟล์' : 'เลือกไฟล์รูปช้าง'}
              <input
                hidden
                type='file'
                accept='image/png,image/jpeg,image/webp'
                aria-label='เลือกไฟล์รูปช้าง'
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  e.target.value = '';
                  if (f) pick(f);
                }}
              />
            </MDButton>
            {file ? (
              <MDTypography variant='caption' display='block' mt={1}>
                ตัวช้างหลังตัดขอบ {num(file.w)} × {num(file.h)} px
              </MDTypography>
            ) : null}
            {tooSmall ? (
              <MDTypography variant='caption' color='error' display='block' role='alert'>
                เล็กเกินไป ต้องอย่างน้อย {MASCOT_MIN_PX} px พิมพ์แล้วจะแตก
              </MDTypography>
            ) : null}
            {small ? (
              <MDTypography variant='caption' color='warning' display='block'>
                ใช้ได้ แต่เล็กกว่าที่แนะนำ พิมพ์แล้วอาจไม่คม
              </MDTypography>
            ) : null}
            {notSquare ? (
              <MDTypography variant='caption' color='warning' display='block'>
                รูปไม่จัตุรัส ระบบย่อให้พอดีช่อง ตัวช้างจะดูเล็กลง
              </MDTypography>
            ) : null}
            {problem ? (
              <MDTypography variant='caption' color='error' display='block' mt={1} role='alert'>
                {problem}
              </MDTypography>
            ) : null}
          </Grid>
          <Grid item xs={12} md={6}>
            <MDTypography variant='caption' fontWeight='bold' display='block' mb={0.5}>
              ตัวอย่าง{ask.item ? ` · ${ask.item.sku}` : ''}
            </MDTypography>
            {ask.item ? (
              <TagMock
                item={{
                  ...ask.item,
                  brand_mascot: 'Y',
                  own_mascot_url: file ? file.url : ask.item.own_mascot_url,
                }}
                mascotUrl={mascotUrl}
                logoUrl={logoUrl}
                stickerUrl={stickerUrl}
                word={word}
                onClick={() => {}}
              />
            ) : null}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {ask.hasOwn ? (
          <MDButton
            variant='text'
            color='error'
            disabled={sending}
            onClick={() => send({ remove: 'Y' })}
            sx={{ mr: 'auto' }}
          >
            เอารูปที่แนบออก
          </MDButton>
        ) : null}
        <MDButton variant='text' color='dark' disabled={sending} onClick={onClose}>
          ยกเลิก
        </MDButton>
        <MDButton
          variant='gradient'
          color='info'
          disabled={sending || !file || tooSmall}
          onClick={() => send({ filedata: file.url })}
        >
          {sending ? 'กำลังบันทึก…' : `ใช้รูปนี้ ${num(ask.ids.length)} สินค้า`}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
MascotDialog.defaultProps = { ask: null, stickerUrl: '', word: '' };
MascotDialog.propTypes = {
  ask: PropTypes.shape({
    ids: PropTypes.arrayOf(PropTypes.number),
    item: PropTypes.shape({ sku: PropTypes.string, own_mascot_url: PropTypes.string }),
    bulk: PropTypes.bool,
    hasOwn: PropTypes.bool,
  }),
  token: PropTypes.string.isRequired,
  tagId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  mascotUrl: PropTypes.string.isRequired,
  logoUrl: PropTypes.string.isRequired,
  stickerUrl: PropTypes.string,
  word: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function TagArtPanel({
  tagId,
  token,
  onNotify,
  onSummary,
  rows,
  onDelete,
  renderOld,
  settings,
  addButtons,
  jump,
  reloadKey,
}) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState('');
  const [runError, setRunError] = useState(''); // /tag_art/run ไม่สำเร็จ
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [page, setPage] = useState(0); // หน้าของแผ่น (ดูทีละ 2 แผ่น)
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // เลขแถว tags_list ที่เปิดแผงแก้ป้าย
  const [picked, setPicked] = useState({}); // {rowId: true} ติ๊กหลายใบ
  const [bulkMenu, setBulkMenu] = useState(null); // anchor เมนู "ทำให้ทุกใบ"
  const [ask, setAsk] = useState(null); // {kind: 'generate' | 'approveAll', count, credits, ids}
  const [reject, setReject] = useState(null); // {item, note}
  const [wordAsk, setWordAsk] = useState(null); // {ids, top, bottom, bulk, manual} — คำโปรที่พิมพ์เอง
  const [viewId, setViewId] = useState(null); // product_id ที่เปิดดูใหญ่
  const [histId, setHistId] = useState(null); // product_id ที่เปิดประวัติภาพ
  const [mascotAsk, setMascotAsk] = useState(null); // {ids, item, bulk, hasOwn} แนบรูปช้างเอง
  const running = useRef(false); // /tag_art/run กำลังทำ — ไม่ยิงซ้อน
  const runBad = useRef(false); // รอบล่าสุดไม่สำเร็จ = ไม่เร่ง
  const lastRun = useRef(0);
  const alive = useRef(true);
  const seq = useRef(0);
  const timer = useRef(null);
  const active = useRef(false); // มีงานค้าง — ถามล้มก็ต้องตั้งนัดถามรอบใหม่ (เดิมหยุดเงียบ วงหมุนค้าง · รีวิว 29 ต.ค.)
  const tagRef = useRef(tagId); // ป้ายที่เปิดอยู่ตอนนี้ — คำตอบของป้ายก่อนหน้า (กด |< < > >| ระหว่างบันทึก) ไม่ทับรายการ
  tagRef.current = tagId;
  /* ชุดสินค้าเปลี่ยน (เพิ่ม/ลบ/เพิ่มตามตำแหน่ง) = โหลดราคา/ช่องใหม่ โดยไม่ล้างหน้า */
  const rowsKey = `${[...new Set(rows.map((r) => Number(r.product_id)))]
    .sort((x, y) => x - y)
    .join(',')}#${rows.length}`;
  const latestKey = useRef(rowsKey);
  latestKey.current = rowsKey;

  const load = () => {
    clearTimeout(timer.current);
    seq.current += 1;
    const mine = seq.current;
    return FetchData('OPTIONS', '/tag_art/list', token, { tag_id: tagId, _t: Date.now() })
      .then((r) => {
        if (!alive.current || mine !== seq.current) return;
        if (r && r.status === 'ok') {
          setData({ ...r, tagId, rowsKey: latestKey.current }); // PromoTagEditor ส่ง tag_id จาก data.tagId
          setFailed('');
          return;
        }
        setFailed(tagArtErrorText(r && r.msg));
        if (active.current) timer.current = setTimeout(load, POLL_MS);
      })
      .catch(() => {
        if (!alive.current || mine !== seq.current) return;
        setFailed('โหลดภาพบนป้ายไม่สำเร็จ');
        if (active.current) timer.current = setTimeout(load, POLL_MS);
      });
  };

  useEffect(() => {
    alive.current = true;
    setData(null);
    running.current = false; // งานของชุดก่อนหน้าค้างอยู่ไม่บังชุดนี้ (server กันซ้อนด้วยล็อกอยู่แล้ว)
    setRunError('');
    setBusy(false); // บันทึกค้างของป้ายก่อนหน้า (กด |< < > >| ระหว่างบันทึก) ไม่ล็อกปุ่มของป้ายนี้ (รีวิว 29 ต.ค.)
    setPicked({});
    setPage(0);
    setQ('');
    setFilter('ALL');
    if (tagId) load();
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
  }, [tagId, token]);

  const rowsSeen = useRef(rowsKey);
  useEffect(() => {
    if (rowsSeen.current === rowsKey) return;
    rowsSeen.current = rowsKey;
    setPicked({});
    if (tagId) load();
  }, [rowsKey]);
  /* บันทึกสาขา/ขนาดป้ายแล้ว ราคาและช่องบนการ์ดเปลี่ยนตาม (เดิมสลับแท็บแล้วโหลดใหม่เอง) */
  const reloadSeen = useRef(reloadKey);
  useEffect(() => {
    if (reloadSeen.current === reloadKey) return;
    reloadSeen.current = reloadKey;
    if (tagId) load();
  }, [reloadKey]);

  const items = (data && data.items) || [];
  const counts = FILTERS.reduce((o, k) => ({ ...o, [k]: 0 }), {});
  items.forEach((it) => {
    counts[statusOf(it)] += 1;
  });
  counts.ALL = items.length;

  /* หน้าเว็บทำงานเอง ไม่รอ cron: ส่งงานให้ AI ทีละนิด + เช็คงานที่ส่งแล้ว แล้วโหลดรายการใหม่ */
  const runThenLoad = () => {
    const forTag = tagId;
    if (running.current) return;
    running.current = true;
    lastRun.current = Date.now();
    FetchData('OPTIONS', '/tag_art/run', token, { tag_id: forTag, _t: Date.now() })
      .catch(() => null)
      .then((r) => {
        if (!alive.current || tagRef.current !== forTag) return;
        running.current = false;
        runBad.current = !(r && r.status === 'ok');
        setRunError(runBad.current ? tagArtErrorText(r && r.msg) : '');
        load();
      });
  };

  /* มีงานค้างในคิว = ทำต่อจนหมด · มีงานยังไม่ได้ส่ง = ทำเลย · ส่งหมดแล้วรอ AI = ถามทุก 8 วิ
     คิวที่ติด AI ปิด/เต็มโควตามีเหตุผลค้างใน error_msg = ไม่เร่ง · บัญชีที่ไม่มีสิทธิ์ AI แค่ดูความคืบหน้า */
  useEffect(() => {
    clearTimeout(timer.current);
    active.current = counts.ACTIVE > 0 || items.some((it) => photoStatusOf(it) === 'ACTIVE');
    if (active.current && !running.current) {
      if (data && data.can_generate === 'Y' && data.ready === 'Y') {
        const toSend = items.some((it) =>
          [it.art, it.photo].some((x) => x && x.status === 'QUEUED' && !x.error_msg)
        );
        const wait =
          toSend && !runBad.current
            ? Math.max(300, 3000 - (Date.now() - lastRun.current))
            : POLL_MS;
        timer.current = setTimeout(runThenLoad, wait);
      } else timer.current = setTimeout(load, POLL_MS);
    }
    if (onSummary && data) {
      /* ช้างของแบรนด์ (mascot_fixed) = ภาพที่ต้องอนุมัติมีแต่รูปสินค้า AI — ตัวเลขบนแถบก่อนพิมพ์/ถามก่อนพิมพ์นับแบบเดียวกัน
         (ยังไม่มี data = ระหว่างโหลด ไม่ส่ง 0 ไปทับ) */
      const photoCount = (s) => items.filter((it) => photoStatusOf(it) === s).length;
      /* แบบช้าง AI: สินค้าที่ภาพช้าง+คำ หรือรูปสินค้า AI รออนุมัติ (Tag.jsx ถามก่อนพิมพ์นับแบบเดียวกัน · ผู้ตรวจ 2 พ.ย.) */
      const anyWaiting = items.filter(
        (it) => statusOf(it) === 'WAITING' || photoStatusOf(it) === 'WAITING'
      ).length;
      /* แถบ "ก่อนพิมพ์" ของ Tag.jsx: พร้อมพิมพ์ / รออนุมัติ / ไม่ได้พิมพ์ นับเป็นใบ (แถว tags_list) */
      const isWaiting = (it) =>
        data && data.mascot_fixed === 'Y'
          ? photoStatusOf(it) === 'WAITING'
          : statusOf(it) === 'WAITING' || photoStatusOf(it) === 'WAITING';
      const byPidAll = Object.fromEntries(items.map((it) => [Number(it.product_id), it]));
      const cards = rows.map((r) => byPidAll[Number(r.product_id)]).filter(Boolean);
      const unprintedCards = cards.filter((it) => it.printable === 'N').length;
      const waitingCards = cards.filter((it) => it.printable !== 'N' && isWaiting(it)).length;
      const canReview =
        data && data.can_generate === 'Y' && data.ready === 'Y'
          ? cards.filter(
              (it) =>
                it.printable !== 'N' &&
                ((photoStatusOf(it) === 'WAITING' && it.photo.can_review === 'Y') ||
                  (data.mascot_fixed !== 'Y' &&
                    statusOf(it) === 'WAITING' &&
                    it.art.can_review === 'Y'))
            ).length
          : 0;
      onSummary({
        ...(data && data.mascot_fixed === 'Y'
          ? { total: counts.ALL, approved: photoCount('APPROVED'), waiting: photoCount('WAITING') }
          : { total: counts.ALL, approved: counts.APPROVED, waiting: anyWaiting }),
        cards: cards.length,
        ready: cards.length - unprintedCards - waitingCards,
        waitingCards,
        canReview,
        unprinted: unprintedCards,
        sheets: Number(data && data.sheets) || 0,
        perSheet: Number(data && data.per_sheet) || 16,
      });
    }
  }, [data]);

  /* แถบ "ก่อนพิมพ์" กด "ตรวจรูป" = เปิดหน้าต่างตรวจทีละใบที่ใบแรกที่รอ · "ดูใบนั้น" = กรองใบที่ไม่ได้พิมพ์ */
  useEffect(() => {
    if (!jump || !data) return;
    if (jump.kind === 'unprinted') {
      setFilter('UNPRINTED');
      setQ('');
      return;
    }
    if (jump.kind === 'review') {
      const fixedNow = data.mascot_fixed === 'Y';
      const first = items.find(
        (it) =>
          it.printable !== 'N' &&
          ((!fixedNow && statusOf(it) === 'WAITING' && it.art.can_review === 'Y') ||
            (photoStatusOf(it) === 'WAITING' && it.photo.can_review === 'Y'))
      );
      if (first) setViewId(first.product_id);
    }
  }, [jump]);

  const canGen = data && data.can_generate === 'Y' && data.ready === 'Y';
  /* ช้างของแบรนด์คงที่ (เจ้าของงาน 1 พ.ย. "ทำตามที่พี่นกบอก" · TagArt::MASCOT_FIXED) — ไม่มีภาพช้าง AI ให้สั่ง/ตรวจ เหลือรูปสินค้า AI */
  const fixed = Boolean(data && data.mascot_fixed === 'Y');
  const aiBlocked =
    (data && (data.ai_ready !== 'Y' ? 'ร้านยังไม่ได้เชื่อมต่อผู้ให้บริการ AI — แจ้งไอที' : '')) ||
    (data && data.ai_off === 'Y'
      ? 'AI ปิดทั้งร้านอยู่ (โควตาเป็น 0 / หยุดฉุกเฉิน) — ผู้ดูแลระบบเปิดได้ที่เมนู AI · ค่าใช้จ่ายและโควตา'
      : '');
  const each = Number((data && data.credits_each) || 4);
  const eachPhoto = Number((data && data.credits_photo) || each); // รูปสินค้า AI คนละโมเดลกับภาพช้าง
  const todo = fixed
    ? []
    : items.filter(
        (it) =>
          ['NONE', 'FAILED'].includes(statusOf(it)) &&
          it.image_url &&
          it.printable !== 'N' &&
          it.ai_blocked !== 'Y' &&
          it.brand_mascot !== 'Y'
      ); // ไม่มีราคาที่สาขานี้ = ไม่ได้พิมพ์ · ซื้อหลายชิ้นไม่ถูกกว่า = ป้ายไม่ใช้ภาพคำชวนประหยัด — ระบบไม่ทำภาพให้ (ไม่คิดเครดิต)
  const photoTodo = items.filter(
    (it) => ['NONE', 'FAILED'].includes(photoStatusOf(it)) && it.image_url && it.printable !== 'N'
  );
  /* ปุ่มเดียว "เจน AI" (4 พ.ย. 2569): ภาพช้าง+คำโปร + รูปสินค้า ของใบที่ยังไม่มี · ชุดช้างแบรนด์ = ระบบสลับเป็นช้าง AI ให้เอง
     ตอนเป็นช้างแบรนด์ server ไม่ส่งสถานะภาพช้างมา จึงนับจากที่รู้ (server ข้ามใบที่มีภาพแล้วเอง = เครดิตจริงไม่เกินที่บอก) */
  const artNeed = (it, ownCard) =>
    ['NONE', 'FAILED'].includes(statusOf(it)) &&
    it.image_url &&
    it.printable !== 'N' &&
    (it.no_save !== 'Y' || isCustom(it.sticker)) &&
    (ownCard || it.brand_mascot !== 'Y');
  const photoNeed = (it) =>
    ['NONE', 'FAILED'].includes(photoStatusOf(it)) && it.image_url && it.printable !== 'N';
  const allPlan = (list, ownCard) => {
    const art = list.filter((it) => artNeed(it, ownCard)).length;
    const photo = list.filter(photoNeed).length;
    return { art, photo, credits: art * each + photo * eachPhoto };
  };
  const allTodo = allPlan(items, false);
  /* ผ่านทั้งหมดที่รอ = ภาพช้าง + รูปสินค้า AI ที่รออนุมัติ */
  const waitIds = [
    ...(fixed ? [] : items)
      .filter((it) => statusOf(it) === 'WAITING' && it.art.can_review === 'Y')
      .map((it) => it.art.id),
    ...items
      .filter((it) => photoStatusOf(it) === 'WAITING' && it.photo.can_review === 'Y')
      .map((it) => it.photo.id),
  ];
  /* ภาพที่ยังรอคิว (ยังไม่ได้ส่งให้ AI) ที่ยกเลิกได้ */
  const queuedIds = [
    ...items
      .filter((it) => it.art && it.art.status === 'QUEUED' && it.art.can_review === 'Y')
      .map((it) => it.art.id),
    ...items
      .filter((it) => it.photo && it.photo.status === 'QUEUED' && it.photo.can_review === 'Y')
      .map((it) => it.photo.id),
  ];
  /* การ์ด = แถว tags_list เรียงตามแผ่น/ช่องที่ไฟล์พิมพ์ใช้ (server คิด slots ให้) · สินค้าเดียวกันหลายแถว = หลายใบ */
  const byPid = Object.fromEntries(items.map((it) => [Number(it.product_id), it]));
  const slots = (data && data.slots) || {};
  const perSheet = Number(data && data.per_sheet) || 16;
  const entries = rows
    .filter((r) => byPid[Number(r.product_id)])
    .map((r) => ({ row: r, item: byPid[Number(r.product_id)], slot: slots[r.id] || null }));
  const placed = entries
    .filter((e) => e.slot)
    .sort((a, b) => a.slot[0] - b.slot[0] || a.slot[1] - b.slot[1]);
  const unprinted = entries.filter((e) => !e.slot);
  const rest = rows.filter((r) => !byPid[Number(r.product_id)]);
  const sheets = Number(data && data.sheets) || 0;
  const pages = Math.max(1, Math.ceil(sheets / SHEETS_PER_PAGE));
  const pg = Math.min(page, pages - 1);
  /* สถานะของใบ (ป้ายบนการ์ด + ตัวกรอง) — เรื่องที่ยังมีผลกับการพิมพ์ก่อน แล้วค่อยรูป AI */
  const cardStatus = (it) => {
    const art = fixed ? 'NONE' : statusOf(it);
    const ph = photoStatusOf(it);
    if (it.printable === 'N') return 'UNPRINTED';
    if (art === 'WAITING' || ph === 'WAITING') return 'WAITING';
    if (art === 'ACTIVE' || ph === 'ACTIVE') return 'ACTIVE';
    if (art === 'FAILED' || ph === 'FAILED') return 'FAILED';
    if (
      it.sticker ||
      it.name_custom === 'Y' ||
      Number(it.custom_levels) > 0 ||
      it.own_mascot_url ||
      (!fixed && it.brand_mascot === 'Y')
    )
      return 'CUSTOM';
    return 'READY';
  };
  const cardCounts = Object.keys(CARD_FILTERS).reduce((o, k) => ({ ...o, [k]: 0 }), {});
  entries.forEach((e) => {
    cardCounts.ALL += 1;
    const k = cardStatus(e.item);
    if (k in cardCounts) cardCounts[k] += 1;
  });
  const needle = q.trim().toLowerCase();
  const matches = (e) =>
    (filter === 'ALL' || cardStatus(e.item) === filter) &&
    (!needle ||
      [e.item.code, e.item.sku, e.item.name, e.item.name_head]
        .join(' ')
        .toLowerCase()
        .includes(needle));
  const narrowed = needle || filter !== 'ALL'; // ค้นหา/กรอง = แสดงที่เจอทั้งหมด ไม่แบ่งแผ่น
  const found = narrowed ? entries.filter(matches) : null;
  /* โหมดตรวจรูป (ก่อนหน้า/ถัดไป) เดินตามลำดับแผ่น/ช่องทั้งชุด ไม่ขึ้นกับตัวกรอง · สินค้าซ้ำนับครั้งเดียว */
  const ordered = [];
  [...placed, ...unprinted].forEach((e) => {
    if (!ordered.includes(e.item)) ordered.push(e.item);
  });
  /* แผงแก้ป้าย: ก่อนหน้า/ถัดไป เดินตามการ์ดทั้งชุด (แผ่น/ช่อง แล้วต่อด้วยใบที่ไม่ได้พิมพ์) */
  const editList = [...placed, ...unprinted];
  const editIdx = editing ? editList.findIndex((e) => e.row.id === editing) : -1;
  const editEntry = editIdx >= 0 ? editList[editIdx] : null;
  const stickers = (data && data.stickers) || [];
  const stickerUrl = (it) => (stickers.find((x) => x.code === stickerOf(it)) || {}).url || '';
  const stickerLabel = (code, text) => {
    if (code === 'ai' || isAiKind(code)) return 'ให้ AI คิดคำ';
    return isCustom(code)
      ? wordText(text)
      : (stickers.find((x) => x.code === code) || {}).label || '';
  };
  const wordOf = (it) => (isCustom(stickerOf(it)) ? it.sticker_text || '' : '');
  const canWord = Boolean(data && data.custom_ready === 'Y');
  const canSticker = Boolean(data && data.can_edit_sticker === 'Y' && stickers.length);
  const canAiWord = Boolean(data && data.ai_word_ready === 'Y');
  /* ตัวเลือกราคาบนป้าย ขึ้นเฉพาะเมื่อมีสินค้าที่มีโปร (นอกนั้นเลือกแล้วป้ายไม่เปลี่ยน) */
  const canLevels = Boolean(
    data && data.can_edit_levels === 'Y' && items.some((it) => Number(it.tiers_total) >= 1)
  );

  const run = (path, body, failPrefix = '') => {
    const forTag = tagId;
    setBusy(true);
    return FetchData('OPTIONS', `/tag_art/${path}`, token, { ...body, _t: Date.now() })
      .then((r) => {
        if (!alive.current || tagRef.current !== forTag) return null; // กด |< < > >| ระหว่างรอ — ไม่โหลดรายการป้ายเก่าทับ
        setBusy(false);
        if (!r || r.status !== 'ok') {
          let text = failPrefix + tagArtErrorText(r && r.msg);
          if (r && r.msg === 'ai_quota_would_exceed')
            text += ` (สั่งได้อีก ${num(r.can_submit)} สินค้า)`;
          onNotify(text, 'error');
          return null;
        }
        load();
        return r;
      })
      .catch(() => {
        if (alive.current && tagRef.current === forTag) {
          setBusy(false);
          onNotify(`${failPrefix}เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง`, 'error');
        }
        return null;
      });
  };

  const generate = (productIds, photo, failPrefix = '') => {
    setAsk(null);
    return run(
      'generate',
      {
        tag_id: tagId,
        ...(productIds ? { product_ids: productIds } : {}),
        ...(photo ? { photo: 'Y' } : {}),
      },
      failPrefix
    ).then((r) => {
      if (!r) return;
      if (r.msg === 'nothing_to_generate')
        onNotify(
          Number(r.no_image) && !Number(r.skipped)
            ? 'สินค้าไม่มีรูป — ทำภาพไม่ได้ (เพิ่มรูปสินค้าก่อน)'
            : ERRORS.nothing_to_generate,
          'warning'
        );
      else
        onNotify(
          `สั่งทำ${photo ? 'รูปสินค้า AI' : 'ภาพ'} ${num(r.count)} สินค้า ≈ ${num(
            r.credits
          )} เครดิต · เสร็จแล้วจะขึ้น "รออนุมัติ"`,
          'success'
        );
    });
  };
  const generateAll = (productIds) => {
    setAsk(null);
    return run('generate_all', {
      tag_id: tagId,
      ...(productIds ? { product_ids: productIds } : {}),
    }).then((r) => {
      if (!r) return;
      const a = Number((r.art && r.art.count) || 0);
      const p = Number((r.photo && r.photo.count) || 0);
      if (!a && !p)
        onNotify(
          Number(r.photo && r.photo.no_image)
            ? 'สินค้าไม่มีรูป — ทำภาพไม่ได้ (เพิ่มรูปสินค้าก่อน)'
            : 'ไม่มีงานเข้าคิว — ทุกใบมีภาพแล้ว หรือกำลังทำอยู่',
          'warning'
        );
      else
        onNotify(
          `สั่งเจน AI แล้ว · ช้าง + คำโปร ${num(a)} ใบ · รูปสินค้า ${num(p)} ใบ ≈ ${num(
            r.credits
          )} เครดิต${
            r.switched === 'Y' ? ' · ชุดนี้เปลี่ยนเป็นแบบช้าง AI แล้ว' : ''
          } · เสร็จแล้วกดผ่าน`,
          'success'
        );
    });
  };
  /* วาดช้าง + คำโปร แล้วทำรูปสินค้า AI (ลบพื้นหลัง/ป้ายที่แปะทับ) ของใบที่ยังไม่มีต่อในรอบเดียว (เจ้าของงาน 2 พ.ย.) */
  const generateBoth = (withPhoto) =>
    generate(null).then(() => (withPhoto ? generate(null, true) : null));
  /* เปลี่ยนสติกเกอร์: ป้ายพิมพ์ครั้งถัดไปใช้ค่าใหม่ทันที · '' = กลับไปให้ระบบเลือกตามราคา */
  /* เปลี่ยนสติกเกอร์: ป้ายพิมพ์ครั้งถัดไปใช้ค่าใหม่ทันที · '' = กลับไปให้ระบบเลือกตามราคา
     ตัดคำตอบของการโหลดรายการที่ค้างอยู่ทิ้ง (seq) แล้วโหลดใหม่หลังบันทึก — กันรายการเก่าทับค่าที่เพิ่งเลือก (รีวิว 29 ต.ค.) */
  const saveSticker = (productIds, code, word) => {
    const forTag = tagId;
    seq.current += 1;
    clearTimeout(timer.current);
    setBusy(true);
    return FetchData('OPTIONS', '/tag_art/sticker', token, {
      tag_id: forTag,
      product_ids: productIds,
      sticker: code,
      ...(word ? { text_top: word.top, text_bottom: word.bottom } : {}),
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current || tagRef.current !== forTag) return null;
        setBusy(false);
        const ok = r && r.status === 'ok';
        if (!ok) {
          onNotify(tagArtErrorText(r && r.msg), 'error');
        } else {
          setData((d) =>
            d
              ? {
                  ...d,
                  items: d.items.map((it) =>
                    productIds.includes(it.product_id)
                      ? { ...it, sticker: r.sticker, sticker_text: r.sticker_text || '' }
                      : it
                  ),
                }
              : d
          );
          if (productIds.length > 1)
            onNotify(`เปลี่ยนคำโปร ${num(productIds.length)} ใบแล้ว`, 'success');
        }
        load();
        return ok ? r : null;
      })
      .catch(() => {
        if (!alive.current || tagRef.current !== forTag) return null;
        setBusy(false);
        onNotify('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง', 'error');
        load();
        return null;
      });
  };
  /* ตั้งคำโปรแล้วสั่งทำภาพช้างตะโกนคำนั้นต่อทันที (พิมพ์เอง / ให้ AI คิด · พี่นก 30 ต.ค.) — ใบที่มีภาพของคำนั้นแล้วระบบข้ามเอง */
  const saveAndGenerate = (productIds, code, word) =>
    saveSticker(productIds, code, word).then((r) => {
      if (r) generate(productIds, false, 'ตั้งคำโปรแล้ว แต่ยังไม่ได้ทำภาพ — ');
    });
  /* ใบที่ระบบทำภาพให้ได้ (มีรูปสินค้า + มีราคา) — ตัวเลขเครดิตบนปุ่ม "+ ทำภาพ" นับเฉพาะใบเหล่านี้ (ผู้ตรวจ 30 ต.ค.) */
  const genIds = (ids) => {
    const want = new Set(ids); // ป้าย 1,000 ใบ — includes ซ้อนใน filter ทำหน้าต่างพิมพ์คำหน่วงทุกตัวอักษร
    return items
      .filter((it) => want.has(it.product_id) && it.image_url && it.printable !== 'N')
      .map((it) => it.product_id);
  };
  /* ให้ AI คิดคำ: มีสิทธิ์ทำภาพ = ถามว่าจะทำภาพเลยไหม · ไม่มี = ตั้งค่าอย่างเดียว */
  const askAiWord = (ids, bulk) => {
    const gen = genIds(ids);
    if (canGen && !aiBlocked && gen.length)
      setAsk({
        kind: 'aiword',
        ids,
        count: ids.length,
        credits: gen.length * each,
        bulk,
        manual: bulk ? items.filter((it) => it.sticker).length : 0,
      });
    else if (bulk)
      setAsk({
        kind: 'sticker',
        code: 'ai',
        count: ids.length,
        ids,
        manual: items.filter((it) => it.sticker).length,
      });
    else saveSticker(ids, 'ai');
  };
  /* จำนวนราคาบนป้ายชุดนี้ (1 = โปรขั้นแรกอย่างเดียว · 2 = 1 ชิ้น + โปรขั้นแรก · 3 = ทุกขั้น) — ป้ายพิมพ์ครั้งถัดไปใช้ค่าใหม่ทันที */
  const saveLevels = (levels) => {
    const forTag = tagId;
    seq.current += 1;
    clearTimeout(timer.current);
    setBusy(true);
    FetchData('OPTIONS', '/tag_art/levels', token, { tag_id: forTag, levels, _t: Date.now() })
      .then((r) => {
        if (!alive.current || tagRef.current !== forTag) return;
        setBusy(false);
        if (!r || r.status !== 'ok') onNotify(tagArtErrorText(r && r.msg), 'error');
        load();
      })
      .catch(() => {
        if (!alive.current || tagRef.current !== forTag) return;
        setBusy(false);
        onNotify('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง', 'error');
        load();
      });
  };
  /* แบบป้ายของชุด (0049): '' = แบบพี่นก · 'ai' = แบบช้าง AI — ไม่เสียเครดิต · พิมพ์ครั้งถัดไปใช้แบบใหม่ทันที */
  const saveStyle = (style) => {
    const forTag = tagId;
    seq.current += 1;
    clearTimeout(timer.current);
    setBusy(true);
    FetchData('OPTIONS', '/tag_art/style', token, { tag_id: forTag, style, _t: Date.now() })
      .then((r) => {
        if (!alive.current || tagRef.current !== forTag) return;
        setBusy(false);
        if (!r || r.status !== 'ok') onNotify(tagArtErrorText(r && r.msg), 'error');
        else
          onNotify(
            style
              ? 'เปลี่ยนเป็นแบบช้าง AI แล้ว · กด "วาดช้าง + คำโปร" ให้ AI วาดภาพของแต่ละสินค้า'
              : 'เปลี่ยนเป็นช้างของแบรนด์แล้ว (ภาพช้าง AI ที่ทำไว้ยังเก็บอยู่)',
            'success'
          );
        load();
      })
      .catch(() => {
        if (!alive.current || tagRef.current !== forTag) return;
        setBusy(false);
        onNotify('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง', 'error');
        load();
      });
  };
  /* ช้างบนป้ายใบนี้ (0050): ภาพ AI ตามแบบป้าย / ช้างของแบรนด์ — ไม่ลบ/ไม่ปฏิเสธภาพ AI (ใช้กับชุดอื่นต่อ) · ไม่เสียเครดิต */
  const histItem = items.find((it) => it.product_id === histId) || null;
  const viewItem = items.find((it) => it.product_id === viewId) || null;
  const approve = (ids) => {
    setAsk(null);
    return run('approve', { ids }).then((r) => {
      if (r) onNotify(`อนุมัติ ${num(r.approved)} ภาพ`, 'success');
      return r;
    });
  };
  /* ยกเลิกคิว — เฉพาะภาพที่ยังไม่ได้ส่งให้ AI (ไม่เสียเครดิต) · ตรวจการใช้งาน 1.3 · 1 พ.ย. 2569 */
  const cancelQueue = (ids) => {
    setAsk(null);
    run('cancel', { ids }).then((r) => {
      if (!r) return;
      onNotify(
        r.cancelled
          ? `ยกเลิกคิว ${num(r.cancelled)} ภาพ · ไม่เสียเครดิต`
          : 'ไม่มีภาพที่ยกเลิกได้ (เริ่มทำไปแล้ว)',
        r.cancelled ? 'success' : 'warning'
      );
    });
  };
  /* redo false = ไม่ผ่านอย่างเดียว / เลิกใช้ภาพที่อนุมัติแล้ว — ไม่ทำใหม่ ไม่เสียเครดิต (ตรวจการใช้งาน 1.7 · 1 พ.ย. 2569) */
  /* ไม่ผ่าน/เลิกใช้/ทำใหม่ — ใช้ทั้งหน้าต่างเหตุผล (setReject) และโหมดตรวจรูปเต็มจอ · id = แถวรูปสินค้า AI (0 = ภาพช้างล่าสุดของใบ) */
  const rejectNow = (target, id, note, redo, was = 'WAITING') =>
    run('reject', {
      tag_id: tagId,
      id: id || target.art.id,
      note,
      redo: redo ? 'Y' : 'N',
    }).then((r) => {
      if (r)
        onNotify(
          redo
            ? `ส่งทำใหม่แล้ว · ${target.sku}`
            : `${was === 'APPROVED' ? 'เลิกใช้ภาพแล้ว' : 'ไม่ผ่านแล้ว'} · ${target.sku}`,
          'success'
        );
      return r;
    });
  const sendReject = (redo) => {
    const note = reject.note.trim();
    if (redo && !note) return;
    rejectNow(reject.item, reject.id, note, redo, reject.status).then((r) => {
      if (r) setReject(null);
    });
  };

  const detail = (it) => {
    const st = statusOf(it);
    const art = it.art || {};
    const lines = [];
    if (photoStatusOf(it) === 'FAILED')
      lines.push([`รูปสินค้า AI: ${tagArtErrorText(it.photo.error_msg)}`, 'error']);
    if (it.own_mascot_url) lines.push(['ใบนี้ใช้รูปช้างที่แนบเอง', 'text']);
    if (fixed) {
      /* ช้างของแบรนด์ — บอกเฉพาะเรื่องที่ยังมีผลกับป้าย */
      if (it.printable === 'N') lines.push(['ไม่มีราคาที่สาขานี้ — ไม่ได้พิมพ์', 'warning']);
      if (!it.image_url) lines.push(['ไม่มีรูปสินค้า — ป้ายจะไม่มีรูป', 'warning']);
    }
    if (fixed) {
      /* ข้ามบรรทัดของภาพช้าง AI */
    } else if (st === 'FAILED') lines.push([tagArtErrorText(art.error_msg), 'error']);
    if (!fixed && st === 'ACTIVE' && art.error_msg)
      lines.push([`รอคิว: ${tagArtErrorText(art.error_msg)}`, 'warning']); // AI ปิด/เต็มโควตา — เปิดแล้วทำต่อเอง
    if (!fixed && it.printable === 'N')
      lines.push(['ไม่มีราคาที่สาขานี้ — ไม่ได้พิมพ์', 'warning']);
    if (!fixed && it.brand_mascot === 'Y' && !it.own_mascot_url && it.printable !== 'N')
      lines.push(['ใบนี้ใช้ช้างของแบรนด์ (ไม่ใช้ภาพ AI)', 'text']);
    if (!fixed && it.ai_blocked === 'Y' && it.printable !== 'N')
      lines.push([
        'ซื้อหลายชิ้นไม่ถูกกว่าซื้อทีละชิ้น — ป้ายพิมพ์ QUANTITY / PRICING ไม่ใช้ภาพคำโปร',
        'text',
      ]);
    if (fixed) {
      /* ข้ามบรรทัดของภาพช้าง AI */
    } else if (st === 'NONE' && isAiKind(it.art_kind) && it.printable !== 'N')
      lines.push([
        `ยังไม่มีภาพช้าง + คำที่ AI คิด${
          aiFallback(it.art_kind) ? ' · ระหว่างนี้ป้ายใช้คำโปรตามราคา' : ''
        }`,
        'text',
      ]);
    else if (
      st === 'NONE' &&
      it.art_kind &&
      it.art_kind !== 'none' &&
      it.printable !== 'N' &&
      it.ai_blocked !== 'Y' &&
      it.brand_mascot !== 'Y'
    )
      lines.push([
        `ยังไม่มีภาพช้าง + ${stickerLabel(it.art_kind, it.sticker_text) || 'คำโปร'}`,
        'text',
      ]);
    if (!fixed && st === 'WAITING' && isAiKind(it.art_kind))
      lines.push(['ตรวจคำที่ AI คิดในภาพก่อนกดผ่าน', 'warning']);
    if (!fixed && st === 'NONE' && !it.image_url)
      lines.push(['ไม่มีรูปสินค้า — ป้ายจะไม่มีรูป', 'warning']);
    const hidden =
      Number(it.tiers_total || 0) -
      Math.max(0, Number(it.price_rows || 0) - (it.price_one === 'N' ? 0 : 1));
    if (it.printable !== 'N' && hidden > 0)
      lines.push([
        `มีโปรอีก ${hidden} ขั้นที่ไม่แสดง${
          data.can_edit_levels === 'Y' && Number(data.price_levels) !== 1
            ? ' (ตั้ง "ราคาบนป้าย" เป็น 3 ราคา)'
            : ''
        }`,
        'text',
      ]);
    if (!fixed && art.note && st !== 'APPROVED') lines.push([`ทำใหม่ตาม: ${art.note}`, 'text']);
    if (!fixed && it.has_approved === 'Y' && st !== 'APPROVED')
      lines.push(['ป้ายยังใช้ภาพที่อนุมัติเดิมจนกว่าภาพใหม่จะผ่าน', 'text']);
    return lines.map(([text, color]) => (
      <MDTypography key={text} variant='caption' color={color} display='block' lineHeight={1.3}>
        {text}
      </MDTypography>
    ));
  };

  const shell = (message, showRows) => (
    <MDBox p={1}>
      {settings ? (
        <MDBox className='card' p={2} mb={2}>
          <MDTypography variant='h6' mb={1}>
            ตั้งค่าทั้งชุด
          </MDTypography>
          {settings}
        </MDBox>
      ) : null}
      <MDBox
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        flexWrap='wrap'
        gap={1.5}
        mb={2}
      >
        <MDBox sx={{ flex: '1 1 auto' }}>{message}</MDBox>
        <MDBox display='flex' gap={1} flexWrap='wrap'>
          {addButtons}
        </MDBox>
      </MDBox>
      {showRows && renderOld ? (
        <Grid container spacing={1}>
          {rows.map((row) => (
            <Grid item key={row.id} xs={12} md={6} lg={4}>
              {renderOld(row)}
            </Grid>
          ))}
        </Grid>
      ) : null}
    </MDBox>
  );
  if (failed && !data)
    return shell(
      <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap'>
        <MDTypography variant='button' color='error' role='alert'>
          {failed} · แสดงการ์ดแบบเดิม
        </MDTypography>
        <MDButton size='small' variant='text' color='info' onClick={load}>
          <Icon>refresh</Icon>&nbsp;ลองใหม่
        </MDButton>
      </MDBox>,
      true
    );
  if (!data) return shell(<CircularProgress size={28} />, false);
  if (data.ready !== 'Y')
    return shell(
      <MDTypography variant='button' color='warning'>
        {ERRORS.tag_art_not_ready}
      </MDTypography>,
      true
    );

  /* ปุ่มบนการ์ด: ใบที่รอ = ผ่าน/ไม่ผ่านตรงนี้เลย (รูปสินค้า AI ก่อน แล้วค่อยภาพช้าง) · ใบอื่น = แก้ใบนี้ */
  const quick = (e) => {
    const it = e.item;
    const ph = it.photo || {};
    const art = it.art || {};
    const photoWait = canGen && photoStatusOf(it) === 'WAITING' && ph.can_review === 'Y';
    const artWait = canGen && !fixed && statusOf(it) === 'WAITING' && art.can_review === 'Y';
    if (photoWait || artWait) {
      const id = photoWait ? ph.id : art.id;
      return (
        <MDBox display='flex' gap={0.5} alignItems='center'>
          <MDButton
            size='small'
            color='success'
            disabled={busy}
            onClick={() => approve([id])}
            aria-label={`ผ่าน ${it.sku}`}
          >
            <Icon>check</Icon>&nbsp;ผ่าน
          </MDButton>
          <MDButton
            size='small'
            variant='outlined'
            color='error'
            disabled={busy}
            onClick={() =>
              setReject({ item: it, id: photoWait ? ph.id : 0, note: '', status: 'WAITING' })
            }
            aria-label={`ไม่ผ่าน ${it.sku}`}
          >
            <Icon>close</Icon>&nbsp;ไม่ผ่าน
          </MDButton>
          <MDButton
            size='small'
            variant='text'
            color='dark'
            iconOnly
            onClick={() => setViewId(it.product_id)}
            aria-label={`ตรวจรูปใหญ่ ${it.sku}`}
            title='ตรวจทีละใบ'
          >
            <Icon>zoom_in</Icon>
          </MDButton>
        </MDBox>
      );
    }
    const one = allPlan([it], true);
    const failedOne = photoStatusOf(it) === 'FAILED' || statusOf(it) === 'FAILED';
    const canAll = canGen && !aiBlocked && (one.art || one.photo);
    return (
      <MDBox display='flex' gap={0.5} alignItems='center' flexWrap='wrap' justifyContent='flex-end'>
        {canAll ? (
          <MDButton
            size='small'
            variant='outlined'
            color='info'
            disabled={busy}
            onClick={() =>
              setAsk({
                kind: 'all',
                ids: [it.product_id],
                item: it,
                art: one.art,
                photo: one.photo,
                credits: one.credits,
              })
            }
            aria-label={`เจน AI ${it.sku}`}
            title='เจนช้าง + คำโปร + รูปสินค้า AI ของใบนี้ในปุ่มเดียว'
          >
            <Icon>auto_awesome</Icon>&nbsp;{failedOne ? 'ลองใหม่' : 'เจน AI'} ≈ {num(one.credits)}
          </MDButton>
        ) : null}
        <MDButton
          size='small'
          variant='outlined'
          color='dark'
          onClick={() => setEditing(e.row.id)}
          aria-label={`แก้ป้าย ${it.sku}`}
        >
          <Icon>edit</Icon>&nbsp;แก้ใบนี้
        </MDButton>
      </MDBox>
    );
  };

  const card = (e) => {
    const st = cardStatus(e.item);
    const rowId = e.row.id;
    return (
      <Grid item key={rowId} xs={12} sm={6} md={4} lg={3}>
        <MDBox
          className='card'
          p={1.5}
          sx={{
            height: '100%',
            outline: picked[rowId] ? '2px solid #1A73E8' : 'none',
          }}
        >
          <MDBox display='flex' alignItems='center' justifyContent='space-between' gap={0.5}>
            <FormControlLabel
              sx={{ m: 0, minWidth: 0 }}
              control={
                <Checkbox
                  size='small'
                  checked={Boolean(picked[rowId])}
                  onChange={(ev) =>
                    setPicked((p) => {
                      const n = { ...p };
                      if (ev.target.checked) n[rowId] = true;
                      else delete n[rowId];
                      return n;
                    })
                  }
                  inputProps={{ 'aria-label': `เลือก ${e.item.sku}` }}
                  sx={{ p: 0.5 }}
                />
              }
              label={
                <MDTypography variant='caption' color='text'>
                  {e.slot
                    ? `ช่อง ${e.slot[1]}${narrowed ? ` · แผ่น ${e.slot[0]}` : ''}`
                    : 'ไม่ได้พิมพ์'}
                </MDTypography>
              }
            />
            <Chip size='small' label={BADGE[st].label} color={BADGE[st].color} />
          </MDBox>
          <MDBox mt={0.5}>
            <TagMock
              item={e.item}
              mascotUrl={data.tag_mascot_url || data.mascot_url}
              logoUrl={data.logo_url || ''}
              stickerUrl={stickerUrl(e.item)}
              word={wordOf(e.item)}
              onClick={() => setEditing(e.row.id)}
            />
          </MDBox>
          <MDBox
            mt={0.5}
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            flexWrap='wrap'
            gap={0.5}
          >
            <MDTypography
              variant='caption'
              color='text'
              fontWeight='medium'
              noWrap
              sx={{ flex: '1 1 96px', minWidth: 0 }}
              title={e.item.name}
            >
              {e.item.code || e.item.sku}
            </MDTypography>
            <MDBox sx={{ flex: 'none', ml: 'auto' }}>{quick(e)}</MDBox>
          </MDBox>
          {detail(e.item)}
        </MDBox>
      </Grid>
    );
  };

  const shownSheets = [];
  for (let s = pg * SHEETS_PER_PAGE + 1; s <= Math.min(sheets, (pg + 1) * SHEETS_PER_PAGE); s += 1)
    shownSheets.push(s);
  const allIds = items.map((it) => it.product_id);
  const pickedEntries = entries.filter((e) => picked[e.row.id]);
  const pickedIds = [...new Set(pickedEntries.map((e) => e.item.product_id))];
  const pickedWait = [
    ...pickedEntries
      .filter((e) => !fixed && statusOf(e.item) === 'WAITING' && e.item.art.can_review === 'Y')
      .map((e) => e.item.art.id),
    ...pickedEntries
      .filter((e) => photoStatusOf(e.item) === 'WAITING' && e.item.photo.can_review === 'Y')
      .map((e) => e.item.photo.id),
  ];
  const pickedWaitIds = [...new Set(pickedWait)];
  const pickedAll = allPlan(
    items.filter((it) => pickedIds.includes(it.product_id)),
    true
  );
  const stickerMenu = (ids, bulk) => (
    <MDInput
      select
      variant='standard'
      label={bulk ? 'คำโปรทุกใบ' : 'คำโปรใบที่เลือก'}
      value=''
      onChange={(ev) =>
        ev.target.value === 'ai'
          ? askAiWord(ids, bulk)
          : ev.target.value === '__word'
          ? setWordAsk({
              ids,
              top: '',
              bottom: '',
              bulk: true,
              manual: items.filter((it) => ids.includes(it.product_id) && it.sticker).length,
            })
          : setAsk({
              kind: 'sticker',
              code: ev.target.value === 'auto' ? '' : ev.target.value,
              count: ids.length,
              ids,
              manual: items.filter((it) => ids.includes(it.product_id) && it.sticker).length,
            })
      }
      InputProps={{ disabled: busy }}
      SelectProps={{
        displayEmpty: true,
        renderValue: () => (bulk ? 'เปลี่ยนทุกใบ…' : 'เปลี่ยนใบที่เลือก…'),
      }}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 190 }}
    >
      <MenuItem value='auto'>อัตโนมัติตามโปรของใบ</MenuItem>
      {stickers.map((x) => (
        <MenuItem key={x.code} value={x.code}>
          {x.label}
        </MenuItem>
      ))}
      {canAiWord ? <MenuItem value='ai'>✦ ให้ AI คิดคำให้</MenuItem> : null}
      {canWord ? <MenuItem value='__word'>อื่นๆ · พิมพ์คำเอง…</MenuItem> : null}
      <MenuItem value='none'>ไม่ใส่คำโปร</MenuItem>
    </MDInput>
  );

  return (
    <MDBox p={1}>
      {/* ── ตั้งค่าทั้งชุด: ที่เดียว ใช้กับทุกใบ ยกเว้นใบที่ตั้งเองไว้ ── */}
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={1}>
          ตั้งค่าทั้งชุด
          <MDTypography component='span' variant='button' color='text' fontWeight='regular'>
            {' '}
            · ใช้กับทุกใบ ยกเว้นใบที่ตั้งเองไว้
            {cardCounts.CUSTOM ? ` (${num(cardCounts.CUSTOM)} ใบ)` : ''}
          </MDTypography>
        </MDTypography>
        {settings}
        <Grid container spacing={2} mt={0.5}>
          {data.can_edit_style === 'Y' ? (
            <Grid item xs={12} md={4}>
              <MDInput
                select
                variant='standard'
                label='ช้างบนป้าย'
                value={data.tag_style === 'ai' ? 'ai' : 'brand'}
                onChange={(e) => {
                  if (!busy) saveStyle(e.target.value === 'ai' ? 'ai' : '');
                }}
                InputProps={{ disabled: busy }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              >
                <MenuItem value='brand'>ช้างของแบรนด์ · ไม่ใช้เครดิต</MenuItem>
                <MenuItem value='ai'>ช้าง AI ถือสินค้า + คำโปร · ≈ {each} เครดิต/ใบ</MenuItem>
              </MDInput>
            </Grid>
          ) : null}
          {canLevels ? (
            <Grid item xs={12} md={4}>
              <MDInput
                select
                variant='standard'
                label='ราคาบนป้าย'
                value={String(data.price_levels || 2)}
                onChange={(e) => {
                  if (!busy) saveLevels(Number(e.target.value));
                }}
                InputProps={{ disabled: busy }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              >
                <MenuItem value='2'>2 ราคา · 1 ชิ้น + โปรขั้นแรก</MenuItem>
                <MenuItem value='1'>ราคาโปรอย่างเดียว · ไม่มี 1 ชิ้น</MenuItem>
                <MenuItem value='3'>3 ราคา · ทุกขั้นโปร</MenuItem>
              </MDInput>
            </Grid>
          ) : null}
          {canSticker && items.length ? (
            <Grid item xs={12} md={4}>
              {stickerMenu(allIds, true)}
            </Grid>
          ) : null}
          {data.can_attach_mascot === 'Y' && items.length ? (
            <Grid item xs={12} display='flex' alignItems='center' flexWrap='wrap' gap={1}>
              <MDButton
                size='small'
                variant='outlined'
                color='info'
                disabled={busy}
                onClick={() =>
                  setMascotAsk({
                    ids: allIds,
                    item: items[0],
                    bulk: true,
                    hasOwn: items.some((it) => it.own_mascot_url),
                  })
                }
              >
                <Icon>add_photo_alternate</Icon>&nbsp;แนบรูปช้างใหม่ทุกใบ
              </MDButton>
              {items.some((it) => it.own_mascot_url) ? (
                <MDTypography variant='caption' color='text'>
                  ใช้รูปที่แนบอยู่ {num(items.filter((it) => it.own_mascot_url).length)} สินค้า
                </MDTypography>
              ) : null}
            </Grid>
          ) : null}
        </Grid>
      </MDBox>

      {canGen && aiBlocked ? (
        <MDTypography variant='caption' color='error' display='block' mb={1} role='status'>
          {aiBlocked}
        </MDTypography>
      ) : null}
      {/* รอคิวเกิน 10 นาทีและตัวทำภาพไม่ขยับ = cron บนเซิร์ฟเวอร์น่าจะไม่ทำงาน (ตรวจการใช้งาน 1.3 · 1 พ.ย. 2569) */}
      {canGen && (counts.ACTIVE > 0 || items.some((it) => photoStatusOf(it) === 'ACTIVE')) ? (
        <MDTypography variant='caption' color='info' display='block' mb={1} role='status'>
          กำลังทำรูป · เปิดหน้านี้ค้างไว้จนเสร็จ
        </MDTypography>
      ) : null}
      {runError ? (
        <MDTypography variant='caption' color='error' display='block' mb={1} role='alert'>
          ทำรูปต่อไม่ได้: {runError} · ลองใหม่เองทุก 8 วินาที
        </MDTypography>
      ) : null}
      {Number(data.queue_stale) > 0 && counts.ACTIVE > 0 ? (
        <MDBox display='flex' alignItems='center' flexWrap='wrap' gap={1} mb={1} role='alert'>
          <MDTypography variant='caption' color='error' fontWeight='medium'>
            ภาพรอคิวเกิน 10 นาที ระบบทำภาพอาจหยุดทำงาน แจ้งไอที
          </MDTypography>
          {canGen && queuedIds.length ? (
            <MDButton
              size='small'
              variant='text'
              color='error'
              disabled={busy}
              onClick={() => setAsk({ kind: 'cancel', count: queuedIds.length, ids: queuedIds })}
            >
              <Icon>block</Icon>&nbsp;ยกเลิกคิวของชุดนี้ ({num(queuedIds.length)})
            </MDButton>
          ) : null}
        </MDBox>
      ) : null}
      {failed ? (
        <MDTypography variant='caption' color='error' display='block' mb={1} role='status'>
          {failed}
          {counts.ACTIVE > 0 ? ' · ลองใหม่เองทุก 8 วินาที' : ''}
          <MDButton size='small' variant='text' color='info' onClick={load}>
            <Icon>refresh</Icon>&nbsp;ลองใหม่
          </MDButton>
        </MDTypography>
      ) : null}
      {data.can_generate !== 'Y' ? (
        <MDTypography variant='caption' color='warning' display='block' mb={1} role='status'>
          บัญชีนี้ดูได้อย่างเดียว — สั่งทำ/อนุมัติรูปต้องมีสิทธิ์สร้างภาพ AI
        </MDTypography>
      ) : null}

      {/* ── แถบเครื่องมือ: ค้นหา · กรองสถานะ · ทำให้ทุกใบ · เพิ่มสินค้า ── */}
      <MDBox
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        flexWrap='wrap'
        gap={1.5}
        mb={2}
      >
        <MDBox display='flex' alignItems='center' flexWrap='wrap' gap={1} sx={{ flex: '1 1 auto' }}>
          {entries.length ? (
            <MDInput
              label='ค้นรหัส/ชื่อ → บอกแผ่นและช่อง'
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{ minWidth: 240 }}
            />
          ) : null}
          <MDBox display='flex' flexWrap='wrap' gap={0.5} role='group' aria-label='กรองตามสถานะ'>
            {Object.keys(CARD_FILTERS)
              .filter((k) => k === 'ALL' || filter === k || cardCounts[k] > 0)
              .map((k) => (
                <Chip
                  key={k}
                  label={`${CARD_FILTERS[k]} ${num(cardCounts[k])}`}
                  color={filter === k ? (k === 'ALL' ? 'info' : BADGE[k].color) : 'default'}
                  variant={filter === k ? 'filled' : 'outlined'}
                  onClick={() => setFilter(k)}
                  aria-pressed={filter === k}
                />
              ))}
          </MDBox>
        </MDBox>
        <MDBox display='flex' alignItems='center' flexWrap='wrap' gap={1}>
          {canGen && items.length ? (
            <>
              <MDButton
                variant='outlined'
                color='dark'
                disabled={busy}
                onClick={(e) => setBulkMenu(e.currentTarget)}
                aria-haspopup='menu'
              >
                ทำให้ทุกใบ&nbsp;<Icon>expand_more</Icon>
              </MDButton>
              <Menu anchorEl={bulkMenu} open={Boolean(bulkMenu)} onClose={() => setBulkMenu(null)}>
                <MenuItem
                  disabled={!(allTodo.art || allTodo.photo) || Boolean(aiBlocked)}
                  onClick={() => {
                    setBulkMenu(null);
                    setAsk({ kind: 'all', ids: null, ...allTodo });
                  }}
                >
                  เจน AI ครบ · ช้าง + คำโปร + รูปสินค้า ใบที่ยังไม่มี (≈ {num(allTodo.credits)}{' '}
                  เครดิต)
                </MenuItem>
                {fixed ? null : (
                  <MenuItem
                    disabled={!todo.length || Boolean(aiBlocked)}
                    onClick={() => {
                      setBulkMenu(null);
                      setAsk({
                        kind: 'generate',
                        count: todo.length,
                        credits: todo.length * each,
                        withPhoto: photoTodo.length > 0,
                      });
                    }}
                  >
                    วาดช้าง + คำโปร ใบที่ยังไม่มี ({num(todo.length)} · ≈ {num(todo.length * each)}{' '}
                    เครดิต)
                  </MenuItem>
                )}
                <MenuItem
                  disabled={!photoTodo.length || Boolean(aiBlocked)}
                  onClick={() => {
                    setBulkMenu(null);
                    setAsk({
                      kind: 'photo',
                      count: photoTodo.length,
                      credits: photoTodo.length * eachPhoto,
                    });
                  }}
                >
                  ทำรูปสินค้า AI ใบที่ยังไม่มี ({num(photoTodo.length)} · ≈{' '}
                  {num(photoTodo.length * eachPhoto)} เครดิต)
                </MenuItem>
                <MenuItem
                  disabled={!waitIds.length}
                  onClick={() => {
                    setBulkMenu(null);
                    setAsk({ kind: 'approveAll', count: waitIds.length, ids: waitIds });
                  }}
                >
                  ผ่านทุกใบที่รออนุมัติ ({num(waitIds.length)})
                </MenuItem>
                {queuedIds.length ? (
                  <MenuItem
                    onClick={() => {
                      setBulkMenu(null);
                      setAsk({ kind: 'cancel', count: queuedIds.length, ids: queuedIds });
                    }}
                  >
                    ยกเลิกคิวรูปที่ยังไม่เริ่ม ({num(queuedIds.length)})
                  </MenuItem>
                ) : null}
              </Menu>
            </>
          ) : null}
          {addButtons}
        </MDBox>
      </MDBox>

      {!entries.length && !rest.length ? (
        <MDTypography variant='button' color='text'>
          ป้ายชุดนี้ยังไม่มีสินค้า · กด &quot;เพิ่มสินค้า&quot; หรือ &quot;เพิ่มตามตำแหน่ง&quot;
        </MDTypography>
      ) : null}

      {found ? (
        <MDBox mb={3}>
          <MDTypography variant='button' color='text' display='block' mb={1}>
            เจอ {num(found.length)} ใบ
            {filter !== 'ALL' ? ` · ${CARD_FILTERS[filter]}` : ''}
            {needle ? ` · "${q.trim()}"` : ''}
          </MDTypography>
          <Grid container spacing={2}>
            {found.map(card)}
          </Grid>
        </MDBox>
      ) : (
        <>
          {sheets > 1 ? (
            <MDBox display='flex' alignItems='center' gap={1} mb={1.5}>
              <MDButton
                size='small'
                variant='outlined'
                color='dark'
                iconOnly
                aria-label='แผ่นก่อนหน้า'
                disabled={pg <= 0}
                onClick={() => setPage(pg - 1)}
              >
                <Icon>chevron_left</Icon>
              </MDButton>
              <MDTypography variant='button'>
                แผ่น {shownSheets[0]}
                {shownSheets.length > 1 ? `–${shownSheets[shownSheets.length - 1]}` : ''} จาก{' '}
                {sheets} · {num(placed.length)} ใบ ({perSheet} ใบ/แผ่น)
              </MDTypography>
              <MDButton
                size='small'
                variant='outlined'
                color='dark'
                iconOnly
                aria-label='แผ่นถัดไป'
                disabled={pg >= pages - 1}
                onClick={() => setPage(pg + 1)}
              >
                <Icon>chevron_right</Icon>
              </MDButton>
            </MDBox>
          ) : null}
          {shownSheets.map((s) => (
            <MDBox key={s} mb={3}>
              <MDTypography variant='h6' mb={1}>
                แผ่นที่ {s} / {sheets}
                <MDTypography component='span' variant='button' color='text' fontWeight='regular'>
                  {`  · ช่อง 1–${
                    placed.filter((e) => e.slot[0] === s).length
                  } · เรียงตามที่จะพิมพ์จริง`}
                </MDTypography>
              </MDTypography>
              <Grid container spacing={2}>
                {placed.filter((e) => e.slot[0] === s).map(card)}
              </Grid>
            </MDBox>
          ))}
          {unprinted.length && pg === pages - 1 ? (
            <MDBox mb={3}>
              <MDTypography variant='h6' mb={1}>
                ไม่ได้พิมพ์
                <MDTypography component='span' variant='button' color='text' fontWeight='regular'>
                  {' '}
                  · ไม่มีราคาที่สาขานี้ — ตั้งราคาที่เมนูสินค้า หรือลบออกจากชุด
                </MDTypography>
              </MDTypography>
              <Grid container spacing={2}>
                {unprinted.map(card)}
              </Grid>
            </MDBox>
          ) : null}
        </>
      )}
      {data.rowsKey === rowsKey && rest.length && renderOld ? (
        <MDBox mt={entries.length ? 2 : 0} mb={3}>
          {entries.length ? (
            <MDTypography variant='button' color='text' display='block' mb={1}>
              ไม่อยู่ในป้ายแบบใหม่ (ไม่ได้พิมพ์) — กดการ์ดเพื่อลบออกจากชุดนี้
            </MDTypography>
          ) : null}
          <Grid container spacing={1}>
            {rest.map((row) => (
              <Grid item key={row.id} xs={12} md={6} lg={4}>
                {renderOld(row)}
              </Grid>
            ))}
          </Grid>
        </MDBox>
      ) : null}

      {/* ── ติ๊กหลายใบ = แถบทำทีเดียว ── */}
      {pickedEntries.length ? (
        <MDBox
          role='toolbar'
          aria-label='ทำกับใบที่เลือก'
          sx={{
            position: 'sticky',
            bottom: 12,
            zIndex: 5,
            bgcolor: '#344767',
            color: '#fff',
            borderRadius: 2,
            px: 2,
            py: 1,
            boxShadow: '0 10px 24px rgba(0,0,0,.25)',
          }}
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={1}
        >
          <MDBox display='flex' alignItems='center' gap={1}>
            <MDTypography variant='button' color='white' fontWeight='bold'>
              เลือก {num(pickedEntries.length)} ใบ
            </MDTypography>
            <MDButton size='small' variant='outlined' color='white' onClick={() => setPicked({})}>
              ล้าง
            </MDButton>
          </MDBox>
          <MDBox display='flex' alignItems='center' flexWrap='wrap' gap={1}>
            {canSticker ? (
              <MDBox sx={{ bgcolor: '#fff', borderRadius: 1, px: 1, pt: 0.25 }}>
                {stickerMenu(pickedIds, false)}
              </MDBox>
            ) : null}
            {data.can_attach_mascot === 'Y' ? (
              <MDButton
                size='small'
                color='white'
                disabled={busy}
                onClick={() => {
                  const chosen = items.filter((it) => pickedIds.includes(it.product_id));
                  setMascotAsk({
                    ids: pickedIds,
                    item: chosen[0],
                    hasOwn: chosen.some((it) => it.own_mascot_url),
                  });
                }}
              >
                <Icon>add_photo_alternate</Icon>&nbsp;แนบรูปช้าง
              </MDButton>
            ) : null}
            {canGen ? (
              <>
                <MDButton
                  size='small'
                  color='white'
                  disabled={busy || !(pickedAll.art || pickedAll.photo) || Boolean(aiBlocked)}
                  onClick={() => setAsk({ kind: 'all', ids: pickedIds, ...pickedAll })}
                >
                  <Icon>auto_awesome</Icon>&nbsp;เจน AI ที่เลือก (≈ {num(pickedAll.credits)} เครดิต)
                </MDButton>
                <MDButton
                  size='small'
                  color='success'
                  disabled={busy || !pickedWaitIds.length}
                  onClick={() => approve(pickedWaitIds)}
                >
                  <Icon>done_all</Icon>&nbsp;ผ่านที่เลือก ({num(pickedWaitIds.length)})
                </MDButton>
              </>
            ) : null}
          </MDBox>
        </MDBox>
      ) : null}

      {editEntry ? (
        <PromoTagEditor
          onAttach={(it) =>
            setMascotAsk({ ids: [it.product_id], item: it, hasOwn: Boolean(it.own_mascot_url) })
          }
          key={editEntry.row.id}
          data={data}
          item={editEntry.item}
          row={editEntry.row}
          slot={editEntry.slot}
          token={token}
          onReject={(it, id, status) => setReject({ item: it, id, note: '', status })}
          onHistory={setHistId}
          stepInfo={{
            hasPrev: editIdx > 0,
            hasNext: editIdx >= 0 && editIdx < editList.length - 1,
          }}
          onStep={(d) => {
            const e = editList[editIdx + d];
            if (e) setEditing(e.row.id);
          }}
          onClose={() => setEditing(null)}
          onChanged={load}
          onSaved={(msg) => {
            setEditing(null);
            load();
            onNotify(msg, 'success');
          }}
          onDelete={(row) => {
            setEditing(null);
            if (onDelete) onDelete(row);
          }}
          onNotify={onNotify}
        />
      ) : null}

      <MascotDialog
        ask={mascotAsk}
        token={token}
        tagId={tagId}
        mascotUrl={data.tag_mascot_url || data.mascot_url || ''}
        logoUrl={data.logo_url || ''}
        stickerUrl={mascotAsk && mascotAsk.item ? stickerUrl(mascotAsk.item) : ''}
        word={mascotAsk && mascotAsk.item ? wordOf(mascotAsk.item) : ''}
        onClose={() => setMascotAsk(null)}
        onDone={(removed, count) => {
          setMascotAsk(null);
          onNotify(
            removed
              ? `เอารูปที่แนบออก ${num(count)} สินค้าแล้ว`
              : `ใช้รูปช้างใหม่ ${num(count)} สินค้าแล้ว`,
            'success'
          );
          load();
        }}
      />

      <TagReview
        open={Boolean(viewItem)}
        startId={viewId}
        items={ordered}
        data={data}
        fixed={fixed}
        canGen={Boolean(canGen)}
        busy={busy}
        aiBlocked={aiBlocked || ''}
        each={each}
        eachPhoto={eachPhoto}
        stickerUrl={stickerUrl}
        wordOf={wordOf}
        onApprove={approve}
        onReject={rejectNow}
        onApproveAll={(ids) => setAsk({ kind: 'approveAll', count: ids.length, ids })}
        paused={Boolean(ask || histId || reject || wordAsk)}
        onEdit={(pid) => {
          const e = entries.find((x) => x.item.product_id === pid);
          setViewId(null);
          if (e) setEditing(e.row.id);
        }}
        onHistory={setHistId}
        onClose={() => setViewId(null)}
      />

      <Dialog open={Boolean(ask)} onClose={() => setAsk(null)} maxWidth='xs' fullWidth>
        {ask ? (
          <>
            <DialogTitle>
              {
                {
                  generate: 'ให้ AI วาดช้าง + คำโปร',
                  photo: 'ทำรูปสินค้า AI',
                  approveAll: 'ผ่านทั้งหมดที่รออนุมัติ',
                  cancel: 'ยกเลิกคิวภาพ',
                  sticker: 'เปลี่ยนคำโปรทุกใบ',
                  aiword: 'ให้ AI คิดคำโปร',
                  all: ask.item
                    ? `เจน AI · ${ask.item.code || ask.item.sku}`
                    : 'เจน AI ครบทุกใบที่ยังไม่มี',
                }[ask.kind]
              }
            </DialogTitle>
            <DialogContent dividers>
              <MDTypography variant='button' color='text' display='block'>
                {ask.kind === 'all'
                  ? `${
                      ask.art
                        ? `ช้าง + คำโปร ${num(ask.art)} ใบ ≈ ${num(ask.art * each)} เครดิต`
                        : ''
                    }${ask.art && ask.photo ? ' · ' : ''}${
                      ask.photo
                        ? `รูปสินค้า AI ${num(ask.photo)} ใบ ≈ ${num(ask.photo * eachPhoto)} เครดิต`
                        : ''
                    } · รวมไม่เกิน ${num(ask.credits)} เครดิต (ใบที่มีภาพอยู่แล้วระบบข้ามให้)${
                      fixed ? ' · ชุดนี้จะเปลี่ยนเป็นแบบช้าง AI' : ''
                    }${
                      ask.item && ask.item.brand_mascot === 'Y'
                        ? ' · ใบนี้จะเลิกใช้ช้างของแบรนด์'
                        : ''
                    } · ใช้เวลาประมาณ 1–2 นาทีต่อใบ เปิดหน้านี้ค้างไว้ เสร็จแล้วกดผ่านป้ายถึงจะเปลี่ยน`
                  : null}
                {ask.kind === 'generate'
                  ? `AI วาดคำโปร + น้องช้างถือ/ใช้สินค้า ให้ ${num(ask.count)} สินค้า ≈ ${num(
                      ask.credits
                    )} เครดิต (สินค้าละ ${each}) · ใช้เวลาอย่างน้อย ${Math.max(
                      1,
                      Math.ceil(ask.count / 8)
                    )} นาที เสร็จแล้วรออนุมัติที่หน้านี้`
                  : null}
                {ask.kind === 'photo'
                  ? `AI ทำรูปสินค้าแบบสตูดิโอพื้นขาวจากรูปของร้าน ${num(ask.count)} สินค้า ≈ ${num(
                      ask.credits
                    )} เครดิต (สินค้าละ ${eachPhoto}) · ตรวจฉลากกับตัวหนังสือบนสินค้าก่อนกดผ่าน`
                  : null}
                {ask.kind === 'approveAll'
                  ? `อนุมัติ ${num(ask.count)} ภาพ · ป้ายที่พิมพ์ต่อจากนี้ใช้ภาพเหล่านี้ทุกสาขา`
                  : null}
                {ask.kind === 'cancel'
                  ? `ยกเลิก ${num(
                      ask.count
                    )} ภาพที่ยังรอคิว · ยังไม่ได้ส่งให้ AI จึงไม่เสียเครดิต · ใบที่มีภาพอนุมัติเดิม ป้ายใช้ภาพเดิมต่อ`
                  : null}
                {ask.kind === 'sticker'
                  ? `${ask.count < items.length ? 'ใบที่เลือก' : 'ทุกใบในป้ายชุดนี้'} (${num(
                      ask.count
                    )} ใบ) เป็น "${
                      ask.code === '' ? 'อัตโนมัติตามราคา' : stickerLabel(ask.code) || 'ไม่ใส่คำโปร'
                    }"${ask.manual ? ` · ${num(ask.manual)} ใบที่เลือกไว้เองจะถูกเปลี่ยนด้วย` : ''}`
                  : null}
                {ask.kind === 'aiword'
                  ? `${
                      ask.bulk
                        ? `ทุกใบในป้ายชุดนี้ (${num(ask.count)} ใบ)`
                        : ask.count > 1
                        ? `ใบที่เลือก (${num(ask.count)} ใบ)`
                        : 'ใบนี้'
                    } · AI คิดคำโปรภาษาอังกฤษให้เข้ากับสินค้าและโปรของใบ แล้ววาดคำนั้นคู่กับน้องช้างถือสินค้า · อ่านคำในภาพก่อนกดผ่าน · ระหว่างรอ ป้ายใช้คำโปรตามราคาไปก่อน${
                      ask.manual ? ` · ${num(ask.manual)} ใบที่เลือกไว้เองจะถูกเปลี่ยนด้วย` : ''
                    }`
                  : null}
              </MDTypography>
              {ask.kind === 'generate' && photoTodo.length ? (
                <FormControlLabel
                  sx={{ mt: 1.5, ml: 0, alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      size='small'
                      checked={Boolean(ask.withPhoto)}
                      onChange={(e) => setAsk({ ...ask, withPhoto: e.target.checked })}
                      sx={{ pt: 0.25 }}
                    />
                  }
                  label={
                    <MDTypography variant='button' color='text'>
                      ทำรูปสินค้า AI ด้วย — พื้นขาวสะอาด ลบป้าย/รหัสที่แปะทับรูป (
                      {num(photoTodo.length)} ใบที่ยังไม่มี · ≈ {num(photoTodo.length * eachPhoto)}{' '}
                      เครดิต)
                    </MDTypography>
                  }
                />
              ) : null}
            </DialogContent>
            <DialogActions>
              <MDButton variant='text' color='dark' autoFocus onClick={() => setAsk(null)}>
                ยกเลิก
              </MDButton>
              {ask.kind === 'aiword' ? (
                <MDButton
                  variant='outlined'
                  color='info'
                  disabled={busy}
                  onClick={() => {
                    setAsk(null);
                    saveSticker(ask.ids, 'ai');
                  }}
                >
                  ตั้งไว้ก่อน
                </MDButton>
              ) : null}
              <MDButton
                color={{ approveAll: 'success', cancel: 'error' }[ask.kind] || 'info'}
                disabled={busy}
                onClick={() => {
                  if (ask.kind === 'aiword') {
                    setAsk(null);
                    saveAndGenerate(ask.ids, 'ai');
                  } else if (ask.kind === 'all') generateAll(ask.ids);
                  else if (ask.kind === 'generate') generateBoth(Boolean(ask.withPhoto));
                  else if (ask.kind === 'photo') generate(null, true);
                  else if (ask.kind === 'approveAll') approve(ask.ids);
                  else if (ask.kind === 'cancel') cancelQueue(ask.ids);
                  else {
                    setAsk(null);
                    saveSticker(ask.ids, ask.code);
                  }
                }}
              >
                {
                  {
                    generate: 'สั่งวาด',
                    photo: 'สั่งทำรูป',
                    all: `เจนเลย · ไม่เกิน ${num(ask.credits)} เครดิต`,
                    approveAll: 'ผ่านทั้งหมด',
                    cancel: 'ยกเลิกคิว',
                    sticker: 'เปลี่ยนทุกใบ',
                    /* ทั้งชุด = เพดาน (ระบบข้ามใบที่มีภาพของคำนี้แล้ว/ไม่มีราคา/ไม่มีรูป · ยอดจริงขึ้นหลังสั่ง) */
                    aiword: `ตั้ง + ทำภาพ ${ask.bulk ? 'ไม่เกิน' : '≈'} ${num(ask.credits)} เครดิต`,
                  }[ask.kind]
                }
              </MDButton>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog open={Boolean(wordAsk)} onClose={() => setWordAsk(null)} maxWidth='xs' fullWidth>
        {wordAsk ? (
          <>
            <DialogTitle>
              พิมพ์คำโปรเอง (ภาษาอังกฤษ)
              <MDTypography variant='caption' color='text' display='block'>
                {wordAsk.bulk
                  ? `${
                      wordAsk.ids.length < items.length ? 'ใบที่เลือก' : 'ทุกใบในป้ายชุดนี้'
                    } (${num(wordAsk.ids.length)} ใบ)${
                      wordAsk.manual
                        ? ` · ${num(wordAsk.manual)} ใบที่เลือกไว้เองจะถูกเปลี่ยนด้วย`
                        : ''
                    }`
                  : 'เฉพาะใบนี้'}
              </MDTypography>
            </DialogTitle>
            <DialogContent dividers>
              <TextField
                fullWidth
                label='บรรทัดบน (ตัวเล็ก · ไม่ใส่ก็ได้)'
                value={wordAsk.top}
                onChange={(e) =>
                  setWordAsk({ ...wordAsk, top: e.target.value.slice(0, data.custom_max || 20) })
                }
                sx={{ mb: 2 }}
              />
              <TextField
                autoFocus
                fullWidth
                label='บรรทัดล่าง (ตัวใหญ่)'
                value={wordAsk.bottom}
                onChange={(e) =>
                  setWordAsk({ ...wordAsk, bottom: e.target.value.slice(0, data.custom_max || 20) })
                }
                helperText={`บรรทัดละไม่เกิน ${data.custom_max || 20} ตัวอักษร · เช่น BEST / DEAL!`}
              />
              {wordAsk.bottom.trim() && !hasThai(wordAsk.top + wordAsk.bottom) ? (
                <MDBox
                  mt={2}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '515 / 157', // ช่องคำโปรของป้าย V7
                    maxWidth: 320,
                    mx: 'auto',
                    border: '1px dashed',
                    borderColor: 'grey.400',
                    borderRadius: 1,
                    containerType: 'inline-size',
                  }}
                >
                  {/* ตัวอย่างคำในช่องคำโปรของป้าย (ไฟล์พิมพ์พิมพ์เป็นตัวหนังสือ) */}
                  <MockWord
                    text={`${wordAsk.top.trim()}\n${wordAsk.bottom.trim()}`}
                    box={{ left: 0, top: 0, width: 100, height: 100 }}
                    wcq={100}
                  />
                </MDBox>
              ) : null}
              <MDTypography variant='caption' color='text' display='block' mt={1}>
                {fixed
                  ? 'ป้ายพิมพ์คำนี้ในช่องคำโปรได้ทันที · ไม่เสียเครดิต'
                  : `ป้ายพิมพ์คำนี้เป็นตัวหนังสือข้างช้างได้ทันที · ภาพช้างตะโกนคำนี้ต้องทำใหม่ สินค้าละ ≈ ${each} เครดิต`}
              </MDTypography>
              {hasThai(wordAsk.top + wordAsk.bottom) ? (
                <MDTypography variant='caption' color='error' display='block' role='alert'>
                  คำโปรใช้ภาษาอังกฤษเท่านั้น
                </MDTypography>
              ) : null}
            </DialogContent>
            <DialogActions>
              <MDButton variant='text' color='dark' onClick={() => setWordAsk(null)}>
                ยกเลิก
              </MDButton>
              <MDButton
                color='info'
                disabled={busy || !wordAsk.bottom.trim() || hasThai(wordAsk.top + wordAsk.bottom)}
                onClick={() => {
                  const word = { top: wordAsk.top.trim(), bottom: wordAsk.bottom.trim() };
                  const { ids } = wordAsk;
                  setWordAsk(null);
                  saveSticker(ids, 'custom', word);
                }}
              >
                {wordAsk.bulk ? 'ใช้กับทุกใบ' : 'บันทึก'}
              </MDButton>
              {canGen && !fixed && !aiBlocked && genIds(wordAsk.ids).length ? (
                <MDButton
                  color='info'
                  variant='gradient'
                  disabled={busy || !wordAsk.bottom.trim() || hasThai(wordAsk.top + wordAsk.bottom)}
                  onClick={() => {
                    const word = { top: wordAsk.top.trim(), bottom: wordAsk.bottom.trim() };
                    const { ids } = wordAsk;
                    setWordAsk(null);
                    saveAndGenerate(ids, 'custom', word);
                  }}
                >
                  บันทึก + ทำภาพ {wordAsk.bulk ? 'ไม่เกิน' : '≈'}{' '}
                  {num(genIds(wordAsk.ids).length * each)} เครดิต
                </MDButton>
              ) : null}
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog open={Boolean(histItem)} onClose={() => setHistId(null)} maxWidth='md' fullWidth>
        {histItem ? (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {fixed ? 'ประวัติรูปสินค้า AI' : 'ประวัติภาพ'} · {histItem.sku}
              <MDTypography variant='caption' color='text' display='block'>
                {fixed
                  ? 'เลือกรูปไหน ป้ายทุกใบของสินค้านี้จะใช้รูปนั้น (ทุกสาขา) · ไม่เสียเครดิต'
                  : 'เลือกภาพไหน ป้ายทุกใบของสินค้านี้ที่ใช้คำโปรเดียวกันจะใช้ภาพนั้น (ทุกสาขา) · ไม่เสียเครดิต'}
              </MDTypography>
            </DialogTitle>
            <DialogContent dividers>
              <ArtHistory
                item={histItem}
                tagId={tagId}
                token={token}
                canUse={Boolean(canGen)}
                mascotUrl={data.tag_mascot_url || data.mascot_url}
                logoUrl={data.logo_url || ''}
                stickerUrl={stickerUrl(histItem)}
                word={wordOf(histItem)}
                onUsed={load}
                onNotify={onNotify}
              />
            </DialogContent>
            <DialogActions>
              <MDButton variant='text' color='dark' onClick={() => setHistId(null)}>
                ปิด
              </MDButton>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog open={Boolean(reject)} onClose={() => setReject(null)} maxWidth='xs' fullWidth>
        {reject ? (
          <>
            <DialogTitle>
              {reject.status === 'APPROVED' ? 'ภาพที่ใช้อยู่' : 'ไม่ผ่าน'} · {reject.item.sku}
              {reject.id ? ' · รูปสินค้า AI' : ''}
            </DialogTitle>
            <DialogContent dividers>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                label='ต้องแก้อะไร (ถ้าจะทำภาพใหม่)'
                value={reject.note}
                onChange={(e) => setReject({ ...reject, note: e.target.value.slice(0, NOTE_MAX) })}
                helperText={
                  reject.status === 'APPROVED'
                    ? `เลิกใช้ = ป้าย${
                        reject.id ? 'กลับไปใช้รูปในระบบ' : 'ใช้ช้างของแบรนด์แทน'
                      } (เลือกภาพเก่ากลับได้ที่ปุ่มประวัติ) · ไม่เสียเครดิต`
                    : `ไม่ผ่านอย่างเดียวไม่เสียเครดิต · ทำใหม่ ≈ ${
                        reject.id ? eachPhoto : each
                      } เครดิต`
                }
              />
              <MDBox display='flex' flexWrap='wrap' gap={0.5} mt={1}>
                {(reject.id
                  ? PHOTO_FIXES
                  : [...QUICK_FIXES, ...(isAiKind(reject.item.art_kind) ? AI_FIXES : [])]
                ).map((q) => (
                  <Chip
                    key={q}
                    size='small'
                    variant='outlined'
                    label={q}
                    onClick={() =>
                      setReject({
                        ...reject,
                        note: (reject.note.trim() ? `${reject.note.trim()} · ${q}` : q).slice(
                          0,
                          NOTE_MAX
                        ),
                      })
                    }
                  />
                ))}
              </MDBox>
            </DialogContent>
            <DialogActions>
              <MDButton variant='text' color='dark' onClick={() => setReject(null)}>
                ยกเลิก
              </MDButton>
              <MDButton
                variant='outlined'
                color='error'
                disabled={busy}
                onClick={() => sendReject(false)}
              >
                {reject.status === 'APPROVED' ? 'เลิกใช้ภาพนี้' : 'ไม่ผ่าน'}
              </MDButton>
              <MDButton
                color='error'
                disabled={busy || !reject.note.trim() || Boolean(aiBlocked)}
                title={reject.note.trim() ? '' : 'พิมพ์สิ่งที่ต้องแก้ก่อน'}
                onClick={() => sendReject(true)}
              >
                {reject.status === 'APPROVED' ? 'ทำภาพใหม่' : 'ไม่ผ่าน · ทำใหม่'} ≈{' '}
                {reject.id ? eachPhoto : each}
              </MDButton>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </MDBox>
  );
}

TagArtPanel.defaultProps = {
  onSummary: null,
  rows: [],
  onDelete: null,
  renderOld: null,
  settings: null,
  addButtons: null,
  jump: null,
  reloadKey: '',
};

TagArtPanel.propTypes = {
  tagId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  token: PropTypes.string.isRequired,
  onNotify: PropTypes.func.isRequired,
  onSummary: PropTypes.func,
  rows: PropTypes.arrayOf(PropTypes.object),
  onDelete: PropTypes.func,
  renderOld: PropTypes.func,
  settings: PropTypes.node,
  addButtons: PropTypes.node,
  jump: PropTypes.shape({ kind: PropTypes.string, n: PropTypes.number }),
  reloadKey: PropTypes.string,
};

/* ป้ายย่อส่วนแบบใหม่ในแท็บ "ข้อมูล" ของป้ายโปร (31 ต.ค. 2569 · เจ้าของงาน: รายการสินค้ายังเป็นการ์ดแบบเก่า ทั้งที่พิมพ์ออกแบบใหม่)
   1 การ์ด = 1 แถว tags_list (สินค้าเดียวกันหลายแถว = หลายใบ เหมือนไฟล์พิมพ์ · "เพิ่มตามตำแหน่ง") · กดการ์ด = onOpen(แถว) หน้าต่างแก้/ลบเดิมของ TagList
   ภาพ/ราคาจาก /tag_art/list ต่อสินค้า โหลดใหม่เมื่อชุดสินค้าเปลี่ยน · ระหว่างโหลดไม่แยกรายการ (กันสินค้าที่เพิ่งเพิ่มขึ้นเป็น "ไม่อยู่ในป้าย")
   รายการที่ไม่มีในป้ายแบบใหม่ / โหลดไม่ได้ / ยังไม่รัน migration = การ์ดเดิม (renderOld) ให้ยังแก้/ลบได้ */
/* ── แก้ป้ายโปรทีละใบ (พี่นก 2 พ.ย. 2569: "สั่งทำ 3–4 พันรายการทีเดียว ถ้าอยากแก้ตัวไหน จะทำยังไง") ──
   ชื่อบนป้าย + จำนวนราคา (0048 · /tag_art/item) · คำโปร (/tag_art/sticker) · รูปสินค้า AI · พิมพ์ใบนี้ใบเดียว · ลบออกจากชุด
   ตัวอย่างซ้ายเปลี่ยนตามที่แก้ทันที (ราคาแต่ละแบบ server คิดมาให้แล้วใน lines_by_level) */
const LEVEL_TEXT = { 1: 'ราคาโปรอย่างเดียว', 2: '2 ราคา · 1 ชิ้น + โปร', 3: '3 ราคา' };
function PromoTagEditor({
  data,
  item,
  row,
  slot,
  token,
  onClose,
  onSaved,
  onChanged,
  onDelete,
  onNotify,
  onStep,
  stepInfo,
  onReject,
  onHistory,
  onAttach,
}) {
  const custom0 = isCustom(item.sticker);
  const [lines0Top, lines0Bottom] = wordLines(custom0 ? item.sticker_text : '');
  const [head, setHead] = useState(item.name_head || '');
  const [detail, setDetail] = useState(item.name_detail || '');
  const [nameAuto, setNameAuto] = useState(item.name_custom !== 'Y');
  const [levels, setLevels] = useState(Number(item.custom_levels) || 0);
  const aiWordOk = data.ai_word_ready === 'Y';
  const sticker0 =
    item.sticker === 'ai' ? (aiWordOk ? 'ai' : '') : custom0 ? 'custom' : item.sticker || '';
  const [sticker, setSticker] = useState(sticker0);
  const [top, setTop] = useState(lines0Top);
  const [bottom, setBottom] = useState(lines0Bottom);
  const [busy, setBusy] = useState(false);
  const brand0 = item.brand_mascot === 'Y' ? 'Y' : 'N';
  const [brand, setBrand] = useState(brand0);
  useEffect(() => setBrand(brand0), [brand0]); // แนบ/เอารูปช้างออกระหว่างเปิดแผง = สถานะเปลี่ยนจากฝั่ง server
  const [askClose, setAskClose] = useState(false);
  const base = useRef({ sticker: sticker0, top: lines0Top, bottom: lines0Bottom });
  useEffect(() => {
    const was = base.current;
    const same =
      sticker === was.sticker &&
      (sticker !== 'custom' || (top.trim() === was.top && bottom.trim() === was.bottom));
    base.current = { sticker: sticker0, top: lines0Top, bottom: lines0Bottom };
    if (same) {
      setSticker(sticker0);
      setTop(lines0Top);
      setBottom(lines0Bottom);
    }
  }, [sticker0, lines0Top, lines0Bottom]);

  const canItem = data.can_edit_item === 'Y';
  const own = item.own_mascot_url || '';
  const canBrand = data.brand_ready === 'Y' && canItem && data.mascot_fixed === 'N' && !own;
  const canSticker = data.can_edit_sticker === 'Y';
  const canAi = data.can_generate === 'Y' && data.ai_ready === 'Y' && data.ai_off !== 'Y';
  const setLv = Number(data.price_levels) || 2;
  const lvEff = levels || setLv;
  const byLv = (item.lines_by_level || {})[lvEff];
  const nameDirty =
    nameAuto !== (item.name_custom !== 'Y') ||
    (!nameAuto && (head.trim() !== item.name_head || detail.trim() !== (item.name_detail || '')));
  const itemDirty = nameDirty || levels !== (Number(item.custom_levels) || 0);
  const word = sticker === 'custom' ? `${top.trim()}\n${bottom.trim()}` : '';
  const stickerDirty =
    sticker !== sticker0 ||
    (sticker === 'custom' && (top.trim() !== lines0Top || bottom.trim() !== lines0Bottom));
  const brandDirty = !own && brand !== brand0;
  const dirty = itemDirty || stickerDirty || brandDirty;
  /* ภาพช้าง AI ผูกกับคำโปร: เปลี่ยนคำ หรือเปลี่ยนจำนวนราคาจนคำอัตโนมัติ/ราคาที่ไม่ถูกกว่าเปลี่ยน = ภาพเดิมใช้ไม่ได้จนกว่าจะบันทึก (ผู้ตรวจ 2 พ.ย.) */
  const artDirty =
    stickerDirty ||
    brandDirty ||
    Boolean(
      byLv &&
        (((sticker === '' || sticker === 'ai') &&
          (byLv.sticker_auto || '') !== (item.sticker_auto || '')) ||
          (byLv.no_save || 'N') !== (item.no_save || 'N'))
    );
  const customBad = sticker === 'custom' && (!bottom.trim() || hasThai(top + bottom));

  const preview = {
    ...item,
    name_head: nameAuto ? item.name_auto_head : head.trim() || item.name_auto_head,
    name_detail: nameAuto
      ? item.name_auto_detail
      : head.trim()
      ? detail.trim()
      : item.name_auto_detail,
    price_lines: byLv ? byLv.lines : item.price_lines,
    price_one: byLv ? byLv.one : item.price_one,
    sticker_auto: byLv && byLv.sticker_auto !== undefined ? byLv.sticker_auto : item.sticker_auto, // คำโปรอัตโนมัติขึ้นกับขั้นที่พิมพ์
    no_save: byLv && byLv.no_save ? byLv.no_save : item.no_save,
    brand_mascot: brand,
    sticker: sticker === 'custom' ? 'c_0000000000' : sticker,
    ...(sticker === 'ai'
      ? {
          art_kind: `ai_${
            (byLv && byLv.sticker_auto !== undefined ? byLv.sticker_auto : item.sticker_auto) ||
            'none'
          }`,
        }
      : {}),
    ...(artDirty ? { art: null, approved_word_url: '', approved_ele_url: '' } : {}),
  };
  const stickerUrl = (data.stickers || []).find((x) => x.code === stickerOf(preview))?.url || '';

  const call = (path, body) =>
    FetchData('OPTIONS', `/tag_art/${path}`, token, { tag_id: data.tagId, ...body, _t: Date.now() })
      .then((r) => {
        if (!r || r.status !== 'ok') throw new Error(tagArtErrorText(r && r.msg));
        return r;
      })
      .catch((e) => {
        throw new Error(e.message || 'เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง');
      });

  const save = async () => {
    setBusy(true);
    let saved = false; // ชื่อ/ราคาบันทึกแล้วแต่คำโปรล้ม = กริดต้องโหลดใหม่ ไม่งั้นการ์ดโชว์ชื่อเดิม (ผู้ตรวจ 2 พ.ย.)
    try {
      if (canItem && itemDirty) {
        await call('item', {
          product_id: item.product_id,
          head: nameAuto ? '' : head,
          detail: nameAuto ? '' : detail,
          levels,
        });
        saved = true;
      }
      if (canBrand && brandDirty) {
        await call('mascot', { product_id: item.product_id, brand });
        saved = true;
      }
      if (canSticker && stickerDirty)
        await call('sticker', {
          product_ids: [item.product_id],
          sticker,
          ...(sticker === 'custom' ? { text_top: top.trim(), text_bottom: bottom.trim() } : {}),
        });
      onSaved(`บันทึกป้าย ${item.code || item.sku} แล้ว · พิมพ์ครั้งถัดไปใช้ค่าใหม่`);
    } catch (e) {
      setBusy(false);
      if (saved) onChanged();
      onNotify(
        saved ? `บันทึกชื่อ/ราคาแล้ว แต่คำโปรยังไม่ได้บันทึก: ${e.message}` : e.message,
        'error'
      );
    }
  };
  const photoAction = async (path, body, msg) => {
    setBusy(true);
    try {
      const r = await call(path, body);
      /* generate ตอบ ok แต่ไม่มีงานเข้าคิว (มีรูปที่ยังใช้อยู่/ไม่มีรูป/ไม่มีราคา) = ไม่ใช่สั่งสำเร็จ (ผู้ตรวจ 2 พ.ย.) */
      if (path === 'generate' && !Number(r.count)) {
        setBusy(false);
        onNotify(
          Number(r.no_image)
            ? 'สินค้าไม่มีรูป — ทำรูป AI ไม่ได้'
            : 'ไม่มีงานเข้าคิว — สินค้านี้มีรูปที่รอ/ใช้อยู่แล้ว',
          'warning'
        );
        return;
      }
      const noEffect =
        (path === 'cancel' && !Number(r.cancelled)) || (path === 'approve' && !Number(r.approved));
      if (noEffect) {
        setBusy(false);
        onChanged();
        onNotify(
          path === 'cancel'
            ? 'ยกเลิกไม่ได้ · งานนี้เริ่มทำไปแล้ว'
            : 'ผ่านไม่ได้ · ภาพนี้เปลี่ยนสถานะไปแล้ว โหลดข้อมูลใหม่ให้แล้ว',
          'warning'
        );
        return;
      }
      if (dirty) {
        /* มีค่าที่แก้ค้าง: ทำงานนี้แล้วแต่คงแผงไว้ ค่าไม่หาย */
        setBusy(false);
        onChanged();
        onNotify(`${msg} · ค่าที่แก้ยังไม่ได้บันทึก`, 'success');
        return;
      }
      onSaved(msg);
    } catch (e) {
      setBusy(false);
      onNotify(e.message, 'error');
    }
  };

  const aiStyle = data.mascot_fixed === 'N';
  const ast = statusOf(item);
  const artText = {
    NONE: 'ยังไม่มีภาพ · ป้ายใช้ช้างของแบรนด์กับคำโปรปกติไปก่อน',
    ACTIVE: 'AI กำลังวาด (รอคิว) · เสร็จแล้วจะขึ้น "รออนุมัติ"',
    WAITING: 'ภาพรออนุมัติ · อ่านคำในภาพก่อนกดผ่าน',
    APPROVED: 'ป้ายใช้ภาพนี้อยู่',
    FAILED: `AI วาดไม่สำเร็จ${
      item.art && item.art.error_msg ? `: ${tagArtErrorText(item.art.error_msg)}` : ''
    }`,
  }[ast];
  const pst = photoStatusOf(item);
  const photoText = {
    NONE: 'ยังไม่มีรูปสินค้า AI · ป้ายใช้รูปหลักของสินค้า',
    ACTIVE: 'กำลังทำรูปสินค้า AI (รอคิว) · เสร็จแล้วจะขึ้น "รออนุมัติ"',
    WAITING: 'รูปสินค้า AI รออนุมัติ · กดผ่านแล้วป้ายจะใช้รูปนี้',
    APPROVED: 'ป้ายใช้รูปสินค้า AI อยู่',
    FAILED: `ทำรูปสินค้า AI ไม่สำเร็จ${
      item.photo && item.photo.error_msg ? `: ${tagArtErrorText(item.photo.error_msg)}` : ''
    }`,
  }[pst];
  const photoNote =
    ['NONE', 'FAILED'].includes(pst) && item.photo_approved_url
      ? ' · ป้ายยังใช้รูปสินค้า AI ที่ผ่านก่อนหน้าอยู่ (เปลี่ยนได้ที่ปุ่มประวัติ)'
      : '';
  const artNote =
    ['NONE', 'FAILED'].includes(ast) && item.has_approved === 'Y'
      ? ' · ป้ายยังใช้ภาพที่ผ่านก่อนหน้าอยู่ (เปลี่ยนได้ที่ปุ่มประวัติ)'
      : '';
  const where = slot ? `แผ่น ${slot[0]} · ช่อง ${slot[1]}` : 'ไม่ได้พิมพ์ (ไม่มีราคาที่สาขานี้)';

  /* แผงแก้ป้ายเลื่อนจากขวา (2 พ.ย. 2569 · อาร์ตบอร์ด 3 ของแบบ Claude Design): ตัวอย่างอยู่บน ช่องตั้งค่าเป็นกลุ่มเรียงลง
     ก่อนหน้า/ถัดไป เลื่อนไปใบข้าง ๆ ตามแผ่น/ช่อง (ปิดเมื่อมีค่าที่ยังไม่บันทึก) */
  const requestClose = () => (dirty ? setAskClose(true) : onClose());
  const section = (title, right, body) => (
    <MDBox pt={2} pb={2} sx={{ borderTop: '1px solid #f0f2f5' }}>
      <MDBox display='flex' alignItems='center' justifyContent='space-between' gap={1} mb={1.25}>
        <MDTypography variant='button' fontWeight='bold'>
          {title}
        </MDTypography>
        {right}
      </MDBox>
      {body}
    </MDBox>
  );
  const seg = (options, value, onPick, ariaLabel) => (
    <MDBox
      role='group'
      aria-label={ariaLabel}
      display='flex'
      sx={{ border: '1px solid #dee2e6', borderRadius: 2, overflow: 'hidden' }}
    >
      {options.map(([v, text]) => {
        const on = v === value;
        return (
          <MDBox
            key={String(v)}
            component='button'
            type='button'
            aria-pressed={on}
            disabled={busy}
            onClick={() => onPick(v)}
            sx={{
              flex: 1,
              minHeight: 36,
              border: 0,
              px: 1,
              font: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
              bgcolor: on ? '#1A73E8' : '#fff',
              color: on ? '#fff' : '#495057',
            }}
          >
            {text}
          </MDBox>
        );
      })}
    </MDBox>
  );
  const stepBtn = (d, label, icon) => (
    <MDButton
      iconOnly
      variant='outlined'
      color='dark'
      size='small'
      aria-label={label}
      title={dirty ? 'บันทึกก่อน แล้วค่อยไปใบอื่น' : label}
      disabled={busy || dirty || !onStep || !(d < 0 ? stepInfo.hasPrev : stepInfo.hasNext)}
      onClick={() => onStep && onStep(d)}
    >
      <Icon>{icon}</Icon>
    </MDButton>
  );

  return (
    <Drawer
      anchor='right'
      open
      onClose={busy ? undefined : requestClose}
      PaperProps={{ sx: { width: 'min(600px, 100%)', maxWidth: '100%' } }}
    >
      <MDBox display='flex' flexDirection='column' sx={{ height: '100%' }}>
        <MDBox
          display='flex'
          alignItems='flex-start'
          justifyContent='space-between'
          gap={1}
          px={3}
          pt={2.5}
          pb={1.5}
          sx={{ borderBottom: '1px solid #f0f2f5' }}
        >
          <MDBox sx={{ minWidth: 0 }}>
            <MDTypography variant='h5'>
              แก้ป้าย {item.code || item.sku}
              <MDTypography component='span' variant='button' color='text' fontWeight='regular'>
                &nbsp;&nbsp;{where}
              </MDTypography>
            </MDTypography>
            <MDTypography variant='caption' color='text' display='block' noWrap title={item.name}>
              ชื่อสินค้าในระบบ: {item.name}
            </MDTypography>
          </MDBox>
          <MDBox display='flex' gap={0.5} sx={{ flex: 'none' }}>
            {stepBtn(-1, 'ใบก่อนหน้า', 'chevron_left')}
            {stepBtn(1, 'ใบถัดไป', 'chevron_right')}
            <MDButton
              iconOnly
              variant='outlined'
              color='dark'
              size='small'
              aria-label='ปิด'
              onClick={requestClose}
              disabled={busy}
            >
              <Icon>close</Icon>
            </MDButton>
          </MDBox>
        </MDBox>

        <MDBox px={3} py={2} sx={{ flex: 1, overflowY: 'auto' }}>
          <MDBox sx={{ maxWidth: 520, mx: 'auto' }}>
            <TagMock
              item={preview}
              mascotUrl={data.tag_mascot_url || data.mascot_url}
              logoUrl={data.logo_url || ''}
              stickerUrl={stickerUrl}
              word={sticker === 'custom' ? word : ''}
              onClick={() => {}}
            />
          </MDBox>
          <MDBox
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            gap={1}
            flexWrap='wrap'
            mt={1}
            mb={1}
          >
            <MDTypography variant='caption' color='text'>
              ตัวอย่างเปลี่ยนตามที่แก้ · ราคาคิดจากราคาสาขาและแคมเปญตอนพิมพ์
            </MDTypography>
            <MDTypography
              variant='caption'
              fontWeight='bold'
              sx={{ color: item.printable === 'N' ? '#b3261e' : dirty ? '#b35c00' : '#2e7d32' }}
            >
              {item.printable === 'N' ? 'ไม่ได้พิมพ์' : dirty ? 'ยังไม่บันทึก' : 'พร้อมพิมพ์'}
            </MDTypography>
          </MDBox>

          {section(
            'ชื่อบนป้าย',
            canItem
              ? seg(
                  [
                    [true, 'อัตโนมัติ'],
                    [false, 'แก้เอง'],
                  ],
                  nameAuto,
                  (v) => {
                    setNameAuto(v);
                    if (v) {
                      setHead(item.name_auto_head || '');
                      setDetail(item.name_auto_detail || '');
                    }
                  },
                  'ชื่อบนป้าย อัตโนมัติหรือแก้เอง'
                )
              : null,
            canItem ? (
              <MDBox display='flex' flexDirection='column'>
                <MDInput
                  label='ชื่อเด่น (ตัวใหญ่)'
                  value={head}
                  onChange={(e) => {
                    setHead(e.target.value);
                    setNameAuto(false);
                  }}
                  fullWidth
                  inputProps={{ maxLength: 160, disabled: busy }}
                  sx={{ mb: 1.5 }}
                />
                <MDInput
                  label='รายละเอียด (ตัวเล็ก · ขนาด/รุ่น · 1 บรรทัด)'
                  value={detail}
                  onChange={(e) => {
                    setDetail(e.target.value);
                    setNameAuto(false);
                  }}
                  fullWidth
                  inputProps={{ maxLength: 200, disabled: busy }}
                />
                <MDTypography variant='caption' color='text' display='block' mt={0.5}>
                  {nameAuto
                    ? 'ระบบย่อชื่อให้จากชื่อในระบบ · พิมพ์ทับได้ ไม่กระทบชื่อสินค้าในระบบ (POS/สต๊อก)'
                    : 'ใช้ชื่อนี้เฉพาะป้ายในชุดนี้ · ชื่อสินค้าในระบบ (POS/สต๊อก) ไม่เปลี่ยน'}
                </MDTypography>
              </MDBox>
            ) : (
              <MDTypography variant='caption' color='text' display='block'>
                {data.item_edit_ready === 'Y'
                  ? 'บัญชีนี้แก้ชื่อ/ราคาบนป้ายไม่ได้'
                  : 'แก้ชื่อ/ราคาต่อใบยังใช้ไม่ได้ — แจ้งไอที'}
              </MDTypography>
            )
          )}

          {canItem && Number(item.tiers_total) > 0
            ? section(
                'ราคาบนป้าย (ใบนี้)',
                null,
                <>
                  {seg(
                    [
                      [0, `ตามชุด · ${LEVEL_TEXT[setLv]}`],
                      ...[1, 2, 3].filter((n) => n !== setLv).map((n) => [n, LEVEL_TEXT[n]]),
                    ],
                    levels,
                    (v) => setLevels(Number(v)),
                    'ราคาบนป้ายใบนี้'
                  )}
                  {byLv && byLv.lines ? (
                    <MDBox display='flex' gap={0.75} flexWrap='wrap' mt={1.25}>
                      {byLv.lines.map((ln, i) => (
                        <MDBox
                          key={`${ln.qty}-${ln.price}`}
                          px={1.25}
                          py={0.5}
                          sx={{
                            borderRadius: 1,
                            bgcolor: i === 0 && byLv.one !== 'N' ? '#f0f2f5' : '#FFEA00',
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {num(ln.qty)} {unitOf(ln, Number(ln.qty))} {num(ln.price)}.-
                        </MDBox>
                      ))}
                      {Number(item.tiers_total) > byLv.lines.length - (byLv.one !== 'N' ? 1 : 0) ? (
                        <MDTypography variant='caption' color='text' alignSelf='center'>
                          · มีโปรอีก{' '}
                          {Number(item.tiers_total) -
                            (byLv.lines.length - (byLv.one !== 'N' ? 1 : 0))}{' '}
                          ขั้นที่ไม่พิมพ์
                        </MDTypography>
                      ) : null}
                    </MDBox>
                  ) : null}
                </>
              )
            : null}

          {canSticker
            ? section(
                'คำโปร',
                null,
                <>
                  <MDInput
                    select
                    value={sticker}
                    onChange={(e) => setSticker(e.target.value)}
                    fullWidth
                    SelectProps={{ displayEmpty: true }}
                    InputProps={{ disabled: busy }}
                    label='คำโปร'
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value=''>อัตโนมัติ (ตามโปรของใบ)</MenuItem>
                    <MenuItem value='none'>ไม่ใส่</MenuItem>
                    {(data.stickers || []).map((x) => (
                      <MenuItem key={x.code} value={x.code}>
                        {STICKER_SHORT[x.code] || x.label}
                      </MenuItem>
                    ))}
                    {aiWordOk ? <MenuItem value='ai'>✦ ให้ AI คิดคำให้</MenuItem> : null}
                    {data.custom_ready === 'Y' ? (
                      <MenuItem value='custom'>พิมพ์คำเอง (ภาษาอังกฤษ)</MenuItem>
                    ) : null}
                  </MDInput>
                  {sticker === 'custom' ? (
                    <MDBox display='flex' gap={1} mt={1.5}>
                      <MDInput
                        label='บรรทัดบน (ไม่ใส่ก็ได้)'
                        value={top}
                        onChange={(e) => setTop(e.target.value)}
                        fullWidth
                        inputProps={{ maxLength: data.custom_max || 20, disabled: busy }}
                      />
                      <MDInput
                        label='บรรทัดล่าง'
                        value={bottom}
                        onChange={(e) => setBottom(e.target.value)}
                        fullWidth
                        inputProps={{ maxLength: data.custom_max || 20, disabled: busy }}
                      />
                    </MDBox>
                  ) : null}
                  {sticker === 'custom' && hasThai(top + bottom) ? (
                    <MDTypography variant='caption' color='error' display='block' mt={0.5}>
                      คำโปรใช้ภาษาอังกฤษเท่านั้น
                    </MDTypography>
                  ) : null}
                </>
              )
            : null}

          {section(
            'รูปสินค้า',
            <Chip
              size='small'
              variant='outlined'
              label={STATUS[pst].label}
              color={STATUS[pst].color}
            />,
            <>
              <MDTypography variant='caption' color='text' display='block' mb={1}>
                {photoNote && pst === 'NONE' ? 'ยังไม่มีรูปใหม่' : photoText}
                {photoNote}
              </MDTypography>
              <MDBox display='flex' gap={1} flexWrap='wrap'>
                {canAi &&
                item.image_url &&
                item.printable !== 'N' &&
                ['NONE', 'FAILED'].includes(pst) ? (
                  <MDButton
                    size='small'
                    variant='outlined'
                    color='info'
                    disabled={busy}
                    onClick={() =>
                      photoAction(
                        'generate',
                        { product_ids: [item.product_id], photo: 'Y' },
                        `สั่งทำรูปสินค้า AI ${
                          item.code || item.sku
                        } แล้ว · เสร็จแล้วจะขึ้น "รออนุมัติ"`
                      )
                    }
                  >
                    <Icon>photo_camera</Icon>&nbsp; ทำรูปสินค้า AI · ≈{' '}
                    {num(data.credits_photo || data.credits_each)} เครดิต
                  </MDButton>
                ) : null}
                {pst === 'WAITING' && item.photo.can_review === 'Y' && data.can_generate === 'Y' ? (
                  <>
                    <MDButton
                      size='small'
                      variant='gradient'
                      color='success'
                      disabled={busy}
                      onClick={() =>
                        photoAction(
                          'approve',
                          { ids: [item.photo.id] },
                          'ผ่านแล้ว · ป้ายใช้รูปสินค้า AI นี้'
                        )
                      }
                    >
                      ผ่าน
                    </MDButton>
                    <MDButton
                      size='small'
                      variant='outlined'
                      color='error'
                      disabled={busy}
                      onClick={() => onReject(item, item.photo.id, 'WAITING')}
                    >
                      ไม่ผ่าน
                    </MDButton>
                  </>
                ) : null}
                {pst === 'APPROVED' &&
                item.photo.can_review === 'Y' &&
                data.can_generate === 'Y' ? (
                  <MDButton
                    size='small'
                    variant='outlined'
                    color='error'
                    disabled={busy}
                    onClick={() => onReject(item, item.photo.id, 'APPROVED')}
                  >
                    ทำรูปใหม่ / เลิกใช้
                  </MDButton>
                ) : null}
                {data.can_generate === 'Y' &&
                item.photo &&
                item.photo.status === 'QUEUED' &&
                item.photo.can_review === 'Y' ? (
                  <MDButton
                    size='small'
                    variant='text'
                    color='error'
                    disabled={busy}
                    onClick={() =>
                      photoAction(
                        'cancel',
                        { ids: [item.photo.id] },
                        'ยกเลิกคิวแล้ว · ไม่เสียเครดิต'
                      )
                    }
                  >
                    ยกเลิกคิว
                  </MDButton>
                ) : null}
                {Number(item.history_count) > 0 ? (
                  <MDButton
                    size='small'
                    variant='text'
                    color='info'
                    disabled={busy}
                    onClick={() => onHistory(item.product_id)}
                  >
                    <Icon>history</Icon>&nbsp;ประวัติ {num(item.history_count)}
                  </MDButton>
                ) : null}
                {!canAi && data.can_generate === 'Y' && pst !== 'WAITING' ? (
                  <MDTypography variant='caption' color='text'>
                    {data.ai_off === 'Y'
                      ? 'AI ปิดทั้งร้านอยู่'
                      : 'ร้านยังไม่ได้เชื่อมต่อ AI — แจ้งไอที'}
                  </MDTypography>
                ) : null}
              </MDBox>
            </>
          )}

          {section(
            'ช้างบนป้าย',
            !aiStyle && !own ? (
              <MDTypography variant='caption' color='text'>
                ตามชุด · ช้างของแบรนด์
              </MDTypography>
            ) : null,
            <>
              {data.can_attach_mascot === 'Y' ? (
                <MDBox display='flex' alignItems='center' gap={1} mb={1} flexWrap='wrap'>
                  {own ? (
                    <MDBox
                      component='img'
                      src={own}
                      alt='รูปช้างที่แนบ'
                      sx={{ width: 48, height: 48, objectFit: 'contain' }}
                    />
                  ) : null}
                  <MDButton
                    size='small'
                    variant='outlined'
                    color='info'
                    disabled={busy}
                    onClick={() => onAttach(item)}
                  >
                    <Icon>add_photo_alternate</Icon>&nbsp;
                    {own ? 'เปลี่ยน/เอารูปช้างออก' : 'แนบรูปช้างใหม่'}
                  </MDButton>
                  {own ? (
                    <MDTypography variant='caption' color='text'>
                      ใบนี้ใช้รูปที่แนบ
                    </MDTypography>
                  ) : null}
                </MDBox>
              ) : null}
              {canBrand
                ? seg(
                    [
                      ['N', 'ช้าง AI (ตามชุด)'],
                      ['Y', 'ช้างของแบรนด์ · ใบนี้ไม่ใช้ภาพ AI'],
                    ],
                    brand,
                    (v) => setBrand(v),
                    'ช้างบนป้ายใบนี้'
                  )
                : null}
              {!aiStyle && !own ? (
                <MDTypography variant='caption' color='text' display='block'>
                  ชุดนี้ใช้ช้างของแบรนด์ทุกใบ · เปลี่ยนเป็นช้าง AI ได้ที่ &quot;ตั้งค่าทั้งชุด&quot;
                </MDTypography>
              ) : null}
              {aiStyle && brand === 'N' ? (
                <MDBox mt={canBrand ? 1.5 : 0}>
                  <MDBox display='flex' alignItems='center' gap={1} mb={0.5}>
                    <MDTypography variant='caption' fontWeight='bold'>
                      ภาพช้าง + คำโปรของใบนี้
                    </MDTypography>
                    <Chip
                      size='small'
                      variant='outlined'
                      label={STATUS[ast].label}
                      color={STATUS[ast].color}
                    />
                  </MDBox>
                  <MDTypography variant='caption' color='text' display='block' mb={1}>
                    {artDirty
                      ? 'บันทึกก่อน แล้วค่อยสั่งทำภาพของคำโปรใหม่'
                      : item.ai_blocked === 'Y'
                      ? 'ซื้อหลายชิ้นไม่ถูกกว่าซื้อทีละชิ้น — ป้ายพิมพ์ QUANTITY / PRICING ไม่ใช้ภาพคำโปร'
                      : `${artNote && ast === 'NONE' ? 'ยังไม่มีภาพใหม่' : artText}${artNote}`}
                  </MDTypography>
                  <MDBox display='flex' gap={1} flexWrap='wrap'>
                    {canAi &&
                    !artDirty &&
                    item.ai_blocked !== 'Y' &&
                    item.brand_mascot !== 'Y' &&
                    item.image_url &&
                    item.printable !== 'N' &&
                    ['NONE', 'FAILED'].includes(ast) ? (
                      <MDButton
                        size='small'
                        variant='outlined'
                        color='info'
                        disabled={busy}
                        onClick={() =>
                          photoAction(
                            'generate',
                            { product_ids: [item.product_id] },
                            `สั่งทำภาพ ${item.code || item.sku} แล้ว · เสร็จแล้วจะขึ้น "รออนุมัติ"`
                          )
                        }
                      >
                        <Icon>auto_awesome</Icon>&nbsp; วาดช้าง + คำโปร · ≈ {num(data.credits_each)}{' '}
                        เครดิต
                      </MDButton>
                    ) : null}
                    {!artDirty &&
                    ast === 'WAITING' &&
                    item.art.can_review === 'Y' &&
                    data.can_generate === 'Y' ? (
                      <>
                        <MDButton
                          size='small'
                          variant='gradient'
                          color='success'
                          disabled={busy}
                          onClick={() =>
                            photoAction(
                              'approve',
                              { ids: [item.art.id] },
                              'ผ่านแล้ว · ป้ายใช้ภาพนี้'
                            )
                          }
                        >
                          ผ่าน
                        </MDButton>
                        <MDButton
                          size='small'
                          variant='outlined'
                          color='error'
                          disabled={busy}
                          onClick={() => onReject(item, 0, 'WAITING')}
                        >
                          ไม่ผ่าน
                        </MDButton>
                      </>
                    ) : null}
                    {!artDirty &&
                    ast === 'APPROVED' &&
                    item.art.can_review === 'Y' &&
                    data.can_generate === 'Y' ? (
                      <MDButton
                        size='small'
                        variant='outlined'
                        color='error'
                        disabled={busy}
                        onClick={() => onReject(item, 0, 'APPROVED')}
                      >
                        ทำภาพใหม่ / เลิกใช้
                      </MDButton>
                    ) : null}
                    {data.can_generate === 'Y' &&
                    item.art &&
                    item.art.status === 'QUEUED' &&
                    item.art.can_review === 'Y' ? (
                      <MDButton
                        size='small'
                        variant='text'
                        color='error'
                        disabled={busy}
                        onClick={() =>
                          photoAction(
                            'cancel',
                            { ids: [item.art.id] },
                            'ยกเลิกคิวแล้ว · ไม่เสียเครดิต'
                          )
                        }
                      >
                        ยกเลิกคิว
                      </MDButton>
                    ) : null}
                  </MDBox>
                </MDBox>
              ) : null}
            </>
          )}
        </MDBox>

        <MDBox
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          gap={1}
          flexWrap='wrap'
          px={3}
          py={1.5}
          sx={{ borderTop: '1px solid #f0f2f5', bgcolor: '#fff' }}
        >
          <MDButton variant='text' color='error' disabled={busy} onClick={() => onDelete(row)}>
            <Icon>delete</Icon>&nbsp;ลบออกจากชุด
          </MDButton>
          <MDBox display='flex' gap={1} flexWrap='wrap'>
            {slot ? (
              <MDButton
                variant='outlined'
                color='info'
                component='a'
                href={`${data.print_url}?only=${item.product_id}&t=${Date.now()}`}
                target='_blank'
                rel='noopener noreferrer'
                disabled={busy || dirty}
                title={
                  dirty
                    ? 'บันทึกก่อน แล้วค่อยพิมพ์'
                    : 'พิมพ์ใบนี้ใบเดียวที่ช่องแรกของกระดาษ · ขนาดจริง 100%'
                }
              >
                <Icon>print</Icon>&nbsp;พิมพ์ใบนี้
              </MDButton>
            ) : null}
            <MDButton
              variant='gradient'
              color='info'
              disabled={busy || !dirty || customBad}
              onClick={save}
            >
              {busy ? <CircularProgress size={16} color='inherit' /> : <Icon>save</Icon>}
              &nbsp;บันทึก
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
      <Dialog open={askClose} onClose={() => setAskClose(false)} maxWidth='xs' fullWidth>
        <DialogTitle>ยังไม่ได้บันทึกค่าที่แก้</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant='button' color='text'>
            ถ้าปิดตอนนี้ ค่าที่แก้ในใบ {item.code || item.sku} จะหาย
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant='text' color='error' onClick={onClose}>
            ปิดโดยไม่บันทึก
          </MDButton>
          <MDButton autoFocus color='info' onClick={() => setAskClose(false)}>
            กลับไปแก้ต่อ
          </MDButton>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}

PromoTagEditor.defaultProps = {
  slot: null,
  onStep: null,
  stepInfo: { hasPrev: false, hasNext: false },
};
PromoTagEditor.propTypes = {
  onAttach: PropTypes.func.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  row: PropTypes.objectOf(PropTypes.any).isRequired,
  slot: PropTypes.arrayOf(PropTypes.number),
  token: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onNotify: PropTypes.func.isRequired,
  onStep: PropTypes.func,
  stepInfo: PropTypes.shape({ hasPrev: PropTypes.bool, hasNext: PropTypes.bool }),
  onReject: PropTypes.func.isRequired,
  onHistory: PropTypes.func.isRequired,
};

export default TagArtPanel;
